import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
/**
 * Import Detail page (prototype `page-import-detail`, `renderImportDetail()`
 * lines 3602–3746) — subtask 05 + the Compare/Export seam wiring (Phase-1 integ).
 *
 * Breadcrumb (`Imports / <filename>`) + title, the 4-card info grid, the
 * pipeline/status row, the contacts table (CORE columns ALWAYS + whichever
 * dynamic annex columns the API returns), the annex bar (hidden unless annex
 * present), the score-legend footer, 10/page pagination, "Back to People Lists",
 * and — inside the Search card since Trello #711 cl.4 — the "Compare to Thrive"
 * trigger (opens the dry-run modal) beside the per-type write CTA ("Add to
 * search", or "Add to Thrive" on a Company Map). The footer carries the
 * selection-scoped twin of that CTA. Selection state lives here; both surfaces
 * are mounted + wired to their hooks.
 *
 * Built against the shared typed imports client (subtask 01) via TanStack Query.
 * The client is INJECTABLE (`client` prop) so component tests run it against MSW
 * / a fake fetch without a live backend (subtask 08 wires the real origin). No
 * inline styles; English UI; design-system tokens only.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import type {
  CompareBuckets,
  ExportIntent,
  ImportContactRow,
  W2Decision,
} from "@nu-assist/contracts";
import { listPageSize } from "@nu-assist/shared-fe";
import { Button, ProgressBar, runShake } from "@nu-assist/ui";

import { type AiReviewClient, createAiReviewClient } from "../../clients/aiReview.client";
import { type AvatarsClient, createAvatarsClient } from "../../clients/avatars.client";
import { type BctClient, createBctClient } from "../../clients/bct.client";
import {
  type ContactsQueryParams,
  ImportsApiError,
  type ImportsClient,
  createImportsClient,
} from "../../clients/imports.client";
import { type PeopleClient, defaultPeopleClient } from "../../clients/people.client";
import { activeFlags } from "../../config/featureFlags";
import { usePreviewModeIfMounted } from "../../features/PreviewProvider";
import { ExportToThrivePanel } from "../../features/imports-export/ExportToThrivePanel";
import {
  type PreAddChoice,
  PreAddConfirmation,
} from "../../features/imports-export/PreAddConfirmation";
import {
  exportNotAddedClause,
  exportNothingWrittenLine,
} from "../../features/imports-export/exportConfirmationCopy";
import { isExportable, willBeWritten } from "../../features/imports-export/isExportable";
import { CompareToThriveModal } from "../../features/imports/CompareToThriveModal";
import { PreCompareConfirmation } from "../../features/imports/PreCompareConfirmation";
import {
  comparePendingCount,
  computeCompareScope,
  shouldSkipPreCompareConfirmation,
} from "../../features/imports/computeCompareScope";
import {
  COMPARE_FIRST_TOAST,
  UNDO_DIFFERENT_PERSON_FAILED,
  personAddNeedsCompareToast,
} from "../../features/imports/conflictActionsCopy";
import { useCompareToThrive } from "../../features/imports/useCompareToThrive";
import {
  PERSON_ADD_MUTATION_KEY,
  type PersonAddVariables,
  personAddInFlight,
} from "../../features/person-panel/PersonActions";
import { PersonPanel } from "../../features/person-panel/PersonPanel";
import { PersonView } from "../../features/person-panel/PersonView";
import { NO_SEARCH_LINKED } from "../../features/person-panel/actionContext";
import { personInListPath } from "../../features/person-panel/personHandle";
import { personShareUrl } from "../../features/person-panel/sharePreview";
import { useListPersonPanel } from "../../features/person-panel/useListPersonPanel";
import { personQueryKey } from "../../features/person-panel/usePerson";
import { relationshipQueryKey } from "../../features/person-panel/useRelationship";
import { pageVisible } from "../../features/useFeatureAccess";
import { searchDetailPath } from "../../routing/paths";
import { personPagePath } from "../../routing/paths";
import { BULLSEYE_PAGE_ID } from "../client-engagement/searches/bullseye/BullseyeTab";

import { TablePagination } from "../../components/TablePagination";
import { useResolveThriveAvatars } from "../../components/avatars/useResolveThriveAvatars";
import type { Criterion } from "./ai-review/CriteriaPanel";
import { aiReviewFailureText, aiReviewWaitingText } from "./ai-review/aiReviewFailure";
import { isRunInFlight, useAiReviewRun, useLatestAiReview } from "./ai-review/useAiReviewRun";
import { ActionArrow } from "./import-detail/ActionArrow";
import { AlreadyInSearchBanner, inSearchCount } from "./import-detail/AlreadyInSearchBanner";
import { BctRunMeta } from "./import-detail/BctRunDetail";
import {
  ContactsTable,
  type ContactsTableSort,
  EMAIL_SORT_KEY,
  NAME_SORT_KEY,
  THRIVE_COLUMN_SORT_KEYS,
  type ThriveColumnsContext,
  rowAvatarUrl,
} from "./import-detail/ContactsTable";
import {
  AiReviewCriteriaBox,
  BctCriteriaBox,
  SearchCriteriaBox,
} from "./import-detail/CriteriaBox";
import { EditableImportTitle } from "./import-detail/EditableImportTitle";
import type { EnrichmentFilter } from "./import-detail/EnrichmentFilterBar";
import { FullHistory } from "./import-detail/FullHistory";
import { ImportStatsPopover } from "./import-detail/ImportStatsPopover";
import { PipelineStatus } from "./import-detail/PipelineStatus";
import type {
  AddToSearchFilter,
  ReviewStatusFilter,
  ThriveIdFilter,
} from "./import-detail/ReviewStatusFilterBar";
import { ScoreLegend } from "./import-detail/ScoreLegend";
import { SearchCard } from "./import-detail/SearchCard";
import {
  AiReviewMeta,
  RawImportMeta,
  ThriveImportMeta,
  TypeMetaLine,
} from "./import-detail/TypeMetaLine";
import { displayTitle } from "./import-detail/importTitle";
import { parseNotesTerms } from "./import-detail/notesSearch";
import { useContactsSelection } from "./import-detail/useContactsSelection";
import { useResolveAvatars } from "./import-detail/useResolveAvatars";
import { TAG_DEF_BY_VALUE, deriveTypeTag, typeLabel } from "./imports/tagDefs";

/**
 * The People Lists route, and the prefix `backToListPath` validates against.
 *
 * A literal rather than a `routing/paths.ts` builder: that module's value is
 * that a builder is DERIVED from a route pattern, and this route has no pattern
 * constant — it is generated from `LEFT_MENU_SECTIONS` via `PAGE_OVERRIDES`, so
 * a builder there would be a free-floating string with none of the anti-drift
 * property the module is named for.
 */
const IMPORTS_LIST_PATH = "/research/imports";

// Debounce for the free-text filters (name / BCT company / BCT position /
// years): one request per typing settle, not per keystroke.
const FILTER_DEBOUNCE_MS = 300;

function defaultClient(): ImportsClient {
  return createImportsClient({
    apiBase: import.meta.env.VITE_API_URL ?? "",
  });
}

/** The latest `value`, updated only after it has been stable for `delayMs`.
 *  Primitive values only — object identities would re-arm the timer every
 *  render and never settle. */
function useDebounced<T extends string | number | null | undefined>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/** `bct_year_*` input string → query param. Only a full 4-digit year
 *  (1000–9999) is forwarded — a partially-typed "20" must not transiently
 *  filter (nor 422) while the user is still typing. */
function parseYearParam(raw: string): number | undefined {
  const n = Number.parseInt(raw.trim(), 10);
  return Number.isInteger(n) && n >= 1000 && n <= 9999 ? n : undefined;
}

function defaultBctClient(): BctClient {
  return createBctClient({
    apiBase: import.meta.env.VITE_API_URL ?? "",
  });
}

/**
 * Mounts `useCompareToThrive` (which auto-starts the dry run) only while the
 * modal is open, so closing it tears the poll down and re-opening starts a fresh
 * run. Kept as a tiny inner component so the hook lifecycle is bound to the
 * modal's mount, not the page's.
 */
export function CompareModalHost({
  client,
  importId,
  contactIds,
  onClose,
  onContinueToExport,
  onCompareSucceeded,
  pollIntervalMs,
  autoStart,
  alreadyExportedCount,
  scopeTotal,
  pendingOverride,
  scope,
  nothingToCompare,
  isBct,
  actionLabel,
}: {
  client: ImportsClient;
  importId: string;
  contactIds?: string[];
  /** Threaded through to the modal, argument and all — see its own prop doc:
   *  closing now hands back the sitting's answers, and a host that narrowed
   *  this to `() => void` would silently drop them. */
  onClose: (w2Decisions?: Record<string, W2Decision>) => void;
  onContinueToExport: (w2Decisions: Record<string, W2Decision>) => void;
  /** Fired once per run reaching `succeeded`. Closes the window where the
   *  page's `compared_at` has not caught up and the add CTA would refuse a
   *  list that HAS been compared.
   *
   *  Carries the run's `buckets` (epic 2026-09-22, sub-task 05) so a caller
   *  can read `already_in_search` for the "N already in search — Remove
   *  them" banner without a second read of `compare.result` — the host
   *  already has it in hand at the exact instant this fires. Optional and
   *  additive: existing callers that take no argument are unaffected. */
  onCompareSucceeded?: (buckets?: CompareBuckets) => void;
  pollIntervalMs?: number;
  autoStart: boolean;
  alreadyExportedCount: number;
  scopeTotal: number;
  pendingOverride?: number;
  scope: "import" | "selection";
  nothingToCompare: boolean;
  isBct?: boolean;
  /** Forwarded verbatim — see `CompareToThriveModal`. The host adds nothing to
   *  it; it appears here only because the host owns the render. */
  actionLabel?: string;
}) {
  const compare = useCompareToThrive({
    client,
    importId,
    contactIds,
    pollIntervalMs,
    autoStart,
  });

  const queryClient = useQueryClient();
  // Start false — the effect tracks the isRunning transition. Initialising to
  // autoStart caused a spurious invalidation on mount when autoStart=false.
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !compare.isRunning) {
      // Compare just reached terminal — the compare worker has written
      // thrive_avatar_url + contact_id_thrive to import_contacts. Invalidate
      // the contacts caches so ExportToThrivePanel and the ContactsTable show
      // the fresh data without a manual page refresh.
      //
      // Coupling note: these keys mirror useExportToThrive.ts and
      // ExportToThrivePanel.tsx. If any key changes, update all sites together
      // (or extract a queryKeys factory).
      // Prefix-match invalidation covers all page/filter variants of each key.
      queryClient.invalidateQueries({ queryKey: ["imports", importId, "contacts"] });
      queryClient.invalidateQueries({ queryKey: ["import-contacts", importId] });
      // The DETAIL too, since cl.4 item 13: `compared_at` is written by this
      // very run, and the Thrive ID column reads it to decide between "Not
      // found" and a dash. Without this the column keeps saying "nobody has
      // looked" after the compare that looked — the API returns the timestamp,
      // the cached detail does not, and only a reload agreed with reality.
      // Found in the browser, not by a test: every unit test mounts the page
      // with a detail that already has its final value.
      queryClient.invalidateQueries({ queryKey: ["import-detail", importId] });
      // `import-contacts-narrowing` is gone with the narrowing query itself.
      // `import-contacts-all` is kept, and honestly it is ALSO an orphan — no
      // `useQuery` in this repo defines that key today. It predates this PR and
      // is left alone rather than removed in a fix for something else: an
      // invalidation for a key nobody registers is a no-op, not a bug, and
      // deleting it belongs in a change that can say why. Flagged by
      // /pr-review iter 2.
      queryClient.invalidateQueries({ queryKey: ["import-contacts-all", importId] });
      // Unmount safety: if Compare reaches terminal on the exact render before
      // the modal unmounts, this effect still fires — but invalidateQueries is a
      // QueryClient call (not React state), so React never warns about updates on
      // unmounted components. The invalidation is harmless.
      // AND TELL THE PAGE DIRECTLY, because every line above is fire-and-forget.
      //
      // The page's add-to-search CTA refuses when `detail.compared_at` is null,
      // and that turns non-null only once the detail invalidation has
      // round-tripped. A recruiter who closes the modal and presses the CTA
      // immediately beats that refetch and gets shaken at over a compare they
      // just watched finish (/pr-review iter 1, Agent C M2 — predicted there,
      // confirmed by `export.spec.ts` F3 the first time the E2E ran on this
      // branch).
      //
      // `compared_at` stays the durable fact; this is the in-session one, and
      // the race is precisely the window where only the second exists.
      if (compare.result?.status === "succeeded") onCompareSucceeded?.(compare.result.buckets);
    }
    wasRunningRef.current = compare.isRunning;
  }, [
    compare.isRunning,
    compare.result?.status,
    compare.result?.buckets,
    importId,
    queryClient,
    onCompareSucceeded,
  ]);

  return (
    <CompareToThriveModal
      api={client}
      importId={importId}
      result={compare.result}
      isRunning={compare.isRunning}
      error={compare.error}
      onClose={onClose}
      onContinueToExport={onContinueToExport}
      onRetry={() => void compare.start()}
      alreadyExportedCount={alreadyExportedCount}
      scopeTotal={scopeTotal}
      pendingOverride={pendingOverride}
      scope={scope}
      nothingToCompare={nothingToCompare}
      isBct={isBct}
      actionLabel={actionLabel}
    />
  );
}

export function ImportDetailPage({
  client,
  aiReviewClient,
  bctClient,
  peopleClient,
  avatarsClient,
}: {
  // Injected in tests (MSW / fake fetch); defaults to the env-configured client.
  client?: ImportsClient;
  // Injected in tests; defaults to the env-configured read-only AI Review client.
  aiReviewClient?: AiReviewClient;
  // Injected in tests; defaults to the env-configured BCT client.
  bctClient?: BctClient;
  // Injected in tests; defaults to the env-configured People client (person panel).
  peopleClient?: PeopleClient;
  // Injected in tests; defaults to the env-configured avatars client. Read only
  // on a list pulled from a Thrive search, whose photos resolve by Thrive id.
  avatarsClient?: AvatarsClient;
}) {
  // Contacts shown per page, resolved per stage from `@nu-assist/shared-fe`
  // (local 20 / dev 50 / prod 100) so this table moves in lockstep with People
  // Lists and Searches. It is NOT only a row count: the same value is the
  // threshold of `shouldSkipPreCompareConfirmation` below, so lowering it below
  // prod also makes the pre-compare dialog appear for smaller imports on local
  // and dev. That coupling is deliberate — the rule's premise is that the
  // recruiter can SEE every row they are about to compare, which is only true
  // against the size this query actually pages by.
  const pageSize = listPageSize();

  const { importId = "" } = useParams<{ importId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  /**
   * Where "back" goes.
   *
   * The listing row hands over the list URL it navigated FROM (`ImportsTable`),
   * so a reader who filtered to Company Maps on page 2 lands back on Company
   * Maps, page 2 — not on an unfiltered list. Browser Back is unaffected either
   * way; this is only for the in-app links, which are pushes to a path.
   *
   * VALIDATED, not merely defaulted, and the prefix check does two jobs:
   *
   * 1. `ImportsListView` has a SECOND host — Search Detail's People Lists tab
   *    — and `ImportsTable` cannot tell which one it is rendering under. A row
   *    clicked there hands over `/client-engagement/searches/5001?tab=contacts`,
   *    and a link that reads "Back to People Lists" must not navigate to a
   *    search page. Returning the reader where they came from is defensible;
   *    the label lying about it is not. Rejecting the foreign path sends them
   *    to the list the label names.
   * 2. `location.state` is same-origin-only, so this is not an exploitable
   *    path — but react-router honours ABSOLUTE urls in `to`, and the value
   *    survives a refresh via `window.history.state`. A prefix check costs one
   *    line and closes the sink rather than reasoning about why it is unreachable.
   *
   * The fallback is not defensive padding either: this page is reachable with
   * no list behind it at all — a pasted link, the AI Review hand-off, the
   * upload wizard's completion, and detail→detail from the Full history trail
   * (`FullHistory` links between imports without carrying state, so the second
   * detail page falls back) — which is also why `navigate(-1)` would be wrong
   * here (it would send those readers back into the wizard).
   *
   * NOT `replace`. Clicking back to the list leaves the detail page in history,
   * so the browser's Back button returns INTO it. That is the pre-existing
   * behaviour of this link and it is left alone deliberately: this change
   * re-targets the link, it does not redefine what Back means on this page.
   */
  const navigatedFrom = (location.state as { from?: string } | null)?.from;
  // Exactly the list route, bare or with a query. A bare `startsWith` would
  // also accept `/research/imports/<otherId>` (a detail page) and
  // `/research/importsAnything` (a hypothetical sibling route). Neither is
  // producible today — `ImportsTable` is the only writer — but a check that
  // exists to be exact should be exact.
  const backToListPath =
    navigatedFrom === IMPORTS_LIST_PATH || navigatedFrom?.startsWith(`${IMPORTS_LIST_PATH}?`)
      ? navigatedFrom
      : IMPORTS_LIST_PATH;
  const pageQueryClient = useQueryClient();
  /**
   * The run that is filling this file, when the recruiter arrived straight from
   * the creator (Trello #711 cl.5 follow-up, 2026-08-26).
   *
   * The page cannot discover this on its own: `GET /ai-reviews/by-import/{id}`
   * resolves through `find_latest_review_id`, which requires the run to be
   * `applied` — so for the whole time this page most needs to say something, it
   * gets a 404. The creator has the id and hands it over on the 202.
   *
   * Absent on a COLD visit (from the imports list, or a shared link). That is
   * the pre-existing experience and is unchanged: a settled file needs no run
   * to render, and a long-failed one still shows only its status pill.
   */
  const arrivedWithRunId = searchParams.get("run");
  const api = useMemo(() => client ?? defaultClient(), [client]);
  const bctApi = useMemo(() => bctClient ?? defaultBctClient(), [bctClient]);
  // Read-only AI Review client — sources the per-cell rationale for the annex
  // columns (the latest applied run) without touching the imports contract.
  const aiReviewApi = useMemo(
    () => aiReviewClient ?? createAiReviewClient({ apiBase: import.meta.env.VITE_API_URL ?? "" }),
    [aiReviewClient]
  );
  const [page, setPage] = useState(1);
  // Active annex sort (by AI criterion score), or null for the default order.
  const [sort, setSort] = useState<ContactsTableSort | null>(null);
  // Contact filters (2026-08-14 PRD) — ALL server-side: the state lives here,
  // rides as query params on the contacts fetches, and `ContactsTable` renders
  // the controls as controlled inputs. Free-text filters are debounced below;
  // the chips apply immediately.
  const [nameQuery, setNameQuery] = useState("");
  // "Keywords in AI notes…" (Trello #779). Only ever non-empty on an
  // AI-reviewed list, because that is the only place its box renders.
  const [notesQuery, setNotesQuery] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [reviewStatusFilter, setReviewStatusFilter] = useState<ReviewStatusFilter>("all");
  // The orthogonal ⚠ Dup. LinkedIn warning chip — composes with the status chip.
  const [dupLinkedinFilter, setDupLinkedinFilter] = useState(false);
  // The fu5 split's second axis + the orthogonal mark (sub-task 01). Both
  // default to inactive so the bar looks and behaves exactly as before until
  // the recruiter reaches for them.
  const [thriveIdFilter, setThriveIdFilter] = useState<ThriveIdFilter>("any");
  const [addToSearchFilter, setAddToSearchFilter] = useState<AddToSearchFilter>("any");
  const [skippedFilter, setSkippedFilter] = useState<boolean | null>(null);
  const [enrichmentFilter, setEnrichmentFilter] = useState<EnrichmentFilter>("all");
  // "Current Position Filters" — unlike the `bct_*` state below, these apply to every
  // import format (they filter the contact's own promoted position).
  const [currentPositionFilter, setCurrentPositionFilter] = useState("");
  const [currentCompanyFilter, setCurrentCompanyFilter] = useState("");
  const [bctCompanyFilter, setBctCompanyFilter] = useState("");
  const [bctPositionFilter, setBctPositionFilter] = useState("");
  const [bctYearFrom, setBctYearFrom] = useState("");
  const [bctYearTo, setBctYearTo] = useState("");
  // The Thrive candidacy stage chip (04), one stage at a time; only offered on
  // a list pulled from a Thrive search.
  const [stageFilter, setStageFilter] = useState<string | null>(null);

  const debouncedName = useDebounced(nameQuery, FILTER_DEBOUNCE_MS);
  const debouncedNotes = useDebounced(notesQuery, FILTER_DEBOUNCE_MS);
  const debouncedEmail = useDebounced(emailFilter, FILTER_DEBOUNCE_MS);
  const debouncedCurrentPosition = useDebounced(currentPositionFilter, FILTER_DEBOUNCE_MS);
  const debouncedCurrentCompany = useDebounced(currentCompanyFilter, FILTER_DEBOUNCE_MS);
  const debouncedBctCompany = useDebounced(bctCompanyFilter, FILTER_DEBOUNCE_MS);
  const debouncedBctPosition = useDebounced(bctPositionFilter, FILTER_DEBOUNCE_MS);
  const debouncedBctYearFrom = useDebounced(bctYearFrom, FILTER_DEBOUNCE_MS);
  const debouncedBctYearTo = useDebounced(bctYearTo, FILTER_DEBOUNCE_MS);

  // The query-param shape shared by the paged fetch and the AI-mode full
  // fetch. `undefined` members are omitted from the query string; the API
  // silently ignores `bct_*` on non-BCT imports, so no gating is needed here.
  const contactFilters = useMemo<ContactsQueryParams>(
    () => ({
      q: debouncedName.trim() || undefined,
      q_email: debouncedEmail.trim() || undefined,
      // Sent raw: the server parses the terms (phrases, punctuation, dedupe).
      // Not gated on `isAiReviewed` here — that is declared further down, and
      // the box that feeds this only renders on AI-reviewed lists anyway.
      q_notes: debouncedNotes.trim() || undefined,
      current_position: debouncedCurrentPosition.trim() || undefined,
      current_company: debouncedCurrentCompany.trim() || undefined,
      bct_company: debouncedBctCompany.trim() || undefined,
      bct_position: debouncedBctPosition.trim() || undefined,
      bct_year_from: parseYearParam(debouncedBctYearFrom),
      bct_year_to: parseYearParam(debouncedBctYearTo),
      // `review_status` replaces the retired `export_status` param (sub-task
      // 10's contract) — the server derives the status, the FE only forwards
      // the chip. `duplicate_linkedin` rides only when the warning chip is ON
      // (the complement has no chip, so `false` is never sent).
      //
      // The 'To review' chip sends `needs_review` INSTEAD of
      // `review_status=to_review` (operator, 2026-09-08): it selects
      // `to_review` UNION the dup-LinkedIn warning, because a row whose export
      // will duplicate a URL needs a human to look at it even though its own
      // identity question is settled and no single `review_status` says so.
      // Sent as a separate param rather than as an eighth status value — the
      // seven partition the import and this deliberately does not.
      review_status:
        reviewStatusFilter === "all" || reviewStatusFilter === "to_review"
          ? undefined
          : reviewStatusFilter,
      needs_review: reviewStatusFilter === "to_review" ? true : undefined,
      duplicate_linkedin: dupLinkedinFilter ? true : undefined,
      // `undefined` removes the param; `false` is a REAL narrowing here (show
      // me the rows I have not skipped), so it must survive the round trip.
      thrive_id_state: thriveIdFilter === "any" ? undefined : thriveIdFilter,
      add_to_search_state: addToSearchFilter === "any" ? undefined : addToSearchFilter,
      skipped: skippedFilter === null ? undefined : skippedFilter,
      enrichment_status: enrichmentFilter === "all" ? undefined : enrichmentFilter,
      stage: stageFilter ?? undefined,
      // Server-side sort: map the FE sentinel key to the API key.
      // Annex/criterion columns send their raw key (e.g. "fit_score"); the
      // backend expects "score__fit_score". NAME_SORT_KEY and EMAIL_SORT_KEY
      // are native columns — no prefix needed.
      sort_key: sort
        ? sort.key === NAME_SORT_KEY
          ? "name"
          : sort.key === EMAIL_SORT_KEY
            ? "email"
            : (THRIVE_COLUMN_SORT_KEYS[sort.key] ?? `score__${sort.key}`)
        : undefined,
      sort_dir: sort?.dir,
    }),
    [
      debouncedName,
      debouncedNotes,
      debouncedEmail,
      debouncedCurrentPosition,
      debouncedCurrentCompany,
      debouncedBctCompany,
      debouncedBctPosition,
      debouncedBctYearFrom,
      debouncedBctYearTo,
      reviewStatusFilter,
      dupLinkedinFilter,
      thriveIdFilter,
      addToSearchFilter,
      skippedFilter,
      enrichmentFilter,
      stageFilter,
      sort,
    ]
  );
  // Stable key for the query keys (object identity churns per render).
  const contactFiltersKey = JSON.stringify(contactFilters);

  // The selection's UNIVERSE, for the cross-page selection model (sub-task
  // 05): the import + the MEMBERSHIP filters. Sort is deliberately excluded —
  // reordering does not change which contacts "all N" names, so it must not
  // clear a cross-page selection the way a real filter change does.
  const { sort_key: _selSortKey, sort_dir: _selSortDir, ...membershipFilters } = contactFilters;
  const selectionFiltersKey = `${importId}::${JSON.stringify(membershipFilters)}`;
  // Whether any membership filter is narrowing the set.
  //
  // It used to be load-bearing for a REFUSAL: `select_all` could say "every
  // contact" and nothing else, so a cross-page selection under active filters
  // had no honest write shape and the bulk CTA was disabled rather than
  // over-write 1,300 contacts while the banner claimed the filtered 230.
  //
  // `_ExportSelectionRequest` now carries a `filters` block compiled by the
  // same `ContactFilters` the contacts READ uses, so the shape exists. What
  // this flag decides today is narrower and purely presentational: whether the
  // scope dialog says "all N in this list" or "all N matching your filters",
  // and whether the request carries the block at all.
  const hasNarrowingFilters = Object.values(membershipFilters).some((v) => v !== undefined);

  // Filtering narrows the set, so an in-range page can fall out of range.
  // The reset rides IN THE SAME state update as the filter change — an
  // effect-based reset would let one render fire a wasted request with the
  // new filter + the old (possibly out-of-range) page (PR #685 iter-1
  // review). Text filters reset on the keystroke (page 1 is always valid
  // while the debounce settles); chips reset on click.
  function withPageReset<T>(set: (value: T) => void): (value: T) => void {
    return (value) => {
      set(value);
      setPage(1);
    };
  }
  const changeNameQuery = withPageReset(setNameQuery);
  const changeNotesQuery = withPageReset(setNotesQuery);
  // What the rows on screen were matched for — the DEBOUNCED text, so the
  // highlight never runs ahead of the fetch it decorates.
  const notesTerms = useMemo(() => parseNotesTerms(debouncedNotes), [debouncedNotes]);
  // Whether anything besides the notes narrows the list. The count line's
  // "of M" is the whole list, so it holds only when the notes search is alone.
  // Asked of `membershipFilters`, like `hasNarrowingFilters` above, so the two
  // can never disagree about what counts as a filter.
  const { q_notes: _notesFilter, ...nonNotesFilters } = membershipFilters;
  const otherFiltersActive = Object.values(nonNotesFilters).some((v) => v !== undefined);
  const changeEmailFilter = withPageReset(setEmailFilter);
  const changeReviewStatusFilter = withPageReset(setReviewStatusFilter);
  const changeDupLinkedinFilter = withPageReset(setDupLinkedinFilter);
  // `withPageReset` matters here: narrowing the list while sitting on page 4
  // would show an empty page for a filter that has results.
  const changeThriveIdFilter = withPageReset(setThriveIdFilter);
  const changeAddToSearchFilter = withPageReset(setAddToSearchFilter);
  const changeSkippedFilter = withPageReset(setSkippedFilter);

  // Inline [Undo] on an Ignored table row (sub-task 11): DELETE .../leave-out,
  // then refetch the contacts so the row leaves the Ignored state on every
  // surface at once — the cell, the chips and the modal tabs all read the same
  // server payload, which is what makes them unable to disagree. Failure is
  // silent-by-refetch: the row simply stays Ignored (the modal's richer
  // per-card error surface owns the loud path).
  const undoLeaveOutFromTable = (contactId: string) => {
    void api.undoLeaveOut(importId, contactId).then(() => {
      void pageQueryClient.invalidateQueries({ queryKey: ["import-contacts", importId] });
      void pageQueryClient.invalidateQueries({ queryKey: ["import-contacts-all", importId] });
    });
  };
  /** Retire one firm-wide "not the same person" pair from the table's marker.
   *
   *  THE ONLY ALWAYS-REACHABLE REVERSAL. The Compare modal's same-URL card was
   *  the sole surface that could call this, and it is not guaranteed to be
   *  there: a pair recorded from a NAME match sits on a URL no Thrive contact
   *  holds, so the run reports it unheld and the card is dropped. The pair
   *  survives — refusing to delete one on a zero observation is deliberate —
   *  and `export_worker` keeps honouring it, forcing a CREATE
   *  (/pr-review iter 1, Agent A, B2).
   *
   *  Invalidates the same two contact queries `undoLeaveOutFromTable` does, so
   *  the warning and its button leave the row together. */
  const undoDifferentPersonFromTable = (contactId: string, thriveId: string) => {
    void api.undoDifferentPerson(importId, contactId, thriveId).then(
      () => {
        void pageQueryClient.invalidateQueries({ queryKey: ["import-contacts", importId] });
        void pageQueryClient.invalidateQueries({ queryKey: ["import-contacts-all", importId] });
      },
      () => {
        // LOUD, unlike the leave-out twin's silent handler beside it. That one
        // fails into a row the recruiter can simply click again; this one is
        // the last surface standing between a permanent decision and nobody
        // being able to reach it, so a silent no-op here is the bug this whole
        // change exists to remove.
        toast.error(UNDO_DIFFERENT_PERSON_FAILED);
      }
    );
  };
  const changeEnrichmentFilter = withPageReset(setEnrichmentFilter);
  const changeStageFilter = withPageReset(setStageFilter);
  const changeCurrentPositionFilter = withPageReset(setCurrentPositionFilter);
  const changeCurrentCompanyFilter = withPageReset(setCurrentCompanyFilter);
  const changeBctCompanyFilter = withPageReset(setBctCompanyFilter);
  const changeBctPositionFilter = withPageReset(setBctPositionFilter);
  const changeBctYearFrom = withPageReset(setBctYearFrom);
  const changeBctYearTo = withPageReset(setBctYearTo);

  // Filters are page-level state now; reset them (and the page) when
  // navigating between imports (the table's key={importId} remount only
  // covers its own DOM).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on import change only.
  useEffect(() => {
    setNameQuery("");
    setNotesQuery("");
    setEmailFilter("");
    setReviewStatusFilter("all");
    setDupLinkedinFilter(false);
    setEnrichmentFilter("all");
    setCurrentPositionFilter("");
    setCurrentCompanyFilter("");
    setBctCompanyFilter("");
    setBctPositionFilter("");
    setBctYearFrom("");
    setBctYearTo("");
    setStageFilter(null);
    setPage(1);
  }, [importId]);

  // The Compare dry-run modal + the Export panel are surfaced on demand. W2
  // decisions chosen in Compare ride request-time into the Export panel (Compare
  // persists nothing); Compare NEVER hard-blocks Export (FR-13 / v37).
  const [compareOpen, setCompareOpen] = useState(false);
  // The pre-compare confirmation (fu4 sub-task 13, Trello #748) interposes
  // before every run: the CTA opens THIS, and only its "Run compare" flips
  // `compareOpen`. Cancel/Escape close it with nothing enqueued.
  const [compareConfirmOpen, setCompareConfirmOpen] = useState(false);
  // True when the person chose "Compare all X contacts" while holding a
  // selection — the RUN scope ignores the selection; the table selection
  // itself is untouched.
  const [compareAll, setCompareAll] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [carriedW2, setCarriedW2] = useState<Record<string, W2Decision>>({});

  const detailQuery = useQuery({
    queryKey: ["import-detail", importId],
    queryFn: () => api.getImport(importId),
    enabled: Boolean(importId),
    // Parse runs async in the import_worker; `parsing` is non-terminal. Poll
    // the canonical parse-status target (GET /imports/{id}) until it settles to
    // `ready`/`failed` so the detail page resolves a `parsing` → terminal
    // transition without a manual refresh (otherwise the status pill would sit
    // on "Parsing…" forever even after the worker finished).
    refetchInterval: (query) => (query.state.data?.status === "parsing" ? 2000 : false),
  });

  const parseStatus = detailQuery.data?.status;

  // No Compare runs when a combined list arrives (epic
  // 2026-10-01-combine-stability-perf, sub-task 01): the list inherits its
  // sources' Thrive results (`CompareResult.inherited`). The "Compare to
  // Thrive" button below still runs one by hand.

  // Associates the Search-card caption with the write CTA for screen readers.

  // An AI-reviewed import has at least one score-kind annex column. Derived from
  // the DETAIL (available before the contacts page) so it can gate the whole
  // client-side flow WITHOUT a circular dependency on the contacts query — and
  // so the server contacts query can be pinned to page 1 (below), leaving the
  // per-page navigation to the client-side slice.
  const isAiReviewed = (detailQuery.data?.annex_columns ?? []).some((c) => c.kind === "score");

  // Is this row an AI Review OUTPUT FILE? Distinct from `isAiReviewed` above,
  // which asks whether score columns resolved. The two normally agree, and the
  // window where they do not is exactly the one the criteria card has to cover:
  // a file still `parsing` has its format from birth and its annex columns only
  // once the apply worker has written them.
  const isAiReviewFormat = detailQuery.data?.import_format === "ai_review";

  // Export lifecycle poll — surfaces the export status + progress + per-contact
  // state on the BARE detail page (independent of the export modal, surviving
  // its close). Derived server-side purely from the import_contacts rows, so the
  // import always "knows" its export state. Polls while non-terminal.
  const exportStatusQuery = useQuery({
    queryKey: ["imports", importId, "export"],
    queryFn: () => api.getExport(importId),
    enabled: Boolean(importId),
    // Poll only while contacts are actually mid-export (some `pending`). We key
    // off the per-contact results, NOT the bare `status` — get_export reports
    // `running` even for a never-exported import (empty active set), which would
    // otherwise make a freshly-parsed import read "Exporting…".
    refetchInterval: (query) => {
      const rs = query.state.data?.results ?? [];
      return rs.some((r) => r.export_status === "pending") ? 1500 : false;
    },
  });
  const exportStatus = exportStatusQuery.data;
  const exportResults = useMemo(() => exportStatus?.results ?? [], [exportStatus]);
  const exportInProgress = exportResults.some((r) => r.export_status === "pending");
  // The contacts THIS run is writing, captured while they are still `pending`
  // (see the completion effect below, which fills it). Declared here because
  // the progress numbers above the effect read it too.
  const runContactIdsRef = useRef<Set<string> | null>(null);
  // Scoped to THIS run, like the completion notice below — `exportResults` is
  // cumulative (`service_export.py` returns every row that ever entered the
  // lifecycle), so `exportResults.length` is the import's history, not the run.
  // The comment here used to claim otherwise and the bar read "12 of 14" for a
  // two-contact run.
  //
  // Derived DURING RENDER, not read off the ref the effect fills. Ref writes do
  // not re-render, so a snapshot taken in the effect is one paint late: the
  // first frame of a run fell back to the cumulative set and showed the wrong
  // pair for up to a poll before correcting itself. Same defect, just brief.
  // The ref still exists for the completion notice, which needs the set to
  // SURVIVE the run going terminal — by then no row is `pending` any more.
  const runScopedResults = useMemo(() => {
    const pending = exportResults.filter((r) => r.export_status === "pending");
    if (pending.length === 0) {
      const seen = runContactIdsRef.current;
      // NO run in flight and no banked set: the honest answer is "no run", not
      // "the whole import". The progress bar is gated on `exportInProgress` so
      // the cumulative fallback was never rendered — but it made
      // `exportRequested` / `exportProcessed` read as the import's lifetime
      // history to any future consumer outside that gate, silently
      // (/pr-review iter 1, Agent A 3(c)).
      return seen ? exportResults.filter((r) => seen.has(r.contact_id)) : [];
    }
    // Widen with whatever the effect has already banked, so rows that finished
    // between polls keep counting toward the denominator.
    const ids = new Set(runContactIdsRef.current ?? []);
    for (const r of pending) ids.add(r.contact_id);
    return exportResults.filter((r) => ids.has(r.contact_id));
  }, [exportResults]);
  const exportRequested = runScopedResults.length;
  const exportProcessed = runScopedResults.filter((r) => r.export_status !== "pending").length;

  // Export completion notice — owned by the PAGE, deliberately (Trello
  // #711 cl.4 + /pr-review iter 1, Agent C C1 / Agent A 3(a)).
  //
  // `useExportToThrive` already toasts on terminal, but it lives inside
  // `ExportToThrivePanel`, which is mounted `{exportOpen && …}`. Close the
  // modal mid-run and the hook unmounts: polling stops and no toast ever fires.
  // The persistent "Export: Done" card this checklist deleted was the only
  // thing covering that path — its own comment said so ("surviving the
  // modal close") — so deleting it without this made a backgrounded export
  // finish in total silence. The page's `exportStatusQuery` polls independently
  // of the modal, so the notice belongs here.
  //
  // ARMED on an observed in-flight run, not on a terminal status. `getExport`
  // returns a terminal status for any import exported at any point in the past,
  // so a naive "terminal → toast" would fire on every page load of an
  // already-exported list. We only speak for a run we watched finish.
  //
  // Suppressed while the panel is open, because the hook toasts there and two
  // toasts for one run is worse than none. The job is still marked handled in
  // that case, so closing the panel afterwards cannot replay it.
  // ARMED, not job-keyed. `job_id` looks like a run identifier and is NOT one:
  // `service_export.py` answers a hardcoded `_DERIVED_JOB_ID` for every run of
  // every import, so "have I already spoken for this job?" is the same question
  // as "have I ever spoken?". `useExportToThrive` hits the same wall and clears
  // its guard on every new run; this effect is armed by an observed in-flight
  // run and disarmed by speaking, which needs no id at all.
  const armedExportRunRef = useRef(false);
  // Set when the HOOK spoke for the run currently in flight. The panel's hook
  // toasts through the page's own `notify`, so this is the page watching itself
  // and is the only honest answer to "has the recruiter already been told?".
  //
  // It replaces a suppression on `exportOpen`, which assumed the hook speaks
  // whenever the panel is mounted. It does not: a panel REOPENED mid-run starts
  // `polling: false` and never watches a run it did not start, so an export the
  // recruiter closed and reopened finished in total silence.
  const hookSpokeRef = useRef(false);
  // The contacts THIS run is writing, captured while they are still `pending`.
  //
  // Load-bearing, and not obvious: `GET /imports/{id}/export` is CUMULATIVE. It
  // returns every contact that has ever entered the lifecycle for this import
  // (`service_export.py` keeps every row whose `export_status` is not null), and
  // `exported_count` counts all of them. Reading that set as "this run" produced
  // three separate lies, all caught by `/pr-review`:
  //
  //   - one failed row from any earlier run made EVERY later run announce
  //     "N contacts failed" and say nothing about the contacts just added —
  //     permanently, for the life of that import;
  //   - a partially-failed run is `succeeded` server-side, so the hook said
  //     "Added 3" while this effect said "2 failed" for the same run, decided
  //     only by whether the panel happened to be open;
  //   - the success count accumulated: adding 3 to a list with 12 already
  //     exported announced 15.
  //
  // A run's own contacts are exactly those pending when it was armed, so the
  // snapshot is taken there and both numbers are scoped to it.
  // Whether the page watched this run begin. `wasIdleRef` starts FALSE on
  // purpose: a page that mounts into a run already in flight has, by
  // definition, not seen it start.
  const sawRunStartRef = useRef(false);
  const wasIdleRef = useRef(false);
  // `addAction` is read at fire time and deliberately absent from the deps: it
  // derives from `detail.source`, which cannot change for a mounted import.
  // (The `biome-ignore` this replaces had stopped suppressing anything once
  // `exportJobId` left the dependency list.)
  //
  // ERROR BOUNDARY, stated because the ref cluster below has no reset for it:
  // if `exportStatusQuery` starts failing mid-run, this effect simply stops
  // receiving new data — `exportInProgress` never flips false, so the terminal
  // branch never runs and the refs stay armed. That is deliberate: TanStack
  // keeps the last successful `data` on error, so the run is still in flight as
  // far as anything we can observe goes, and disarming would lose the notice
  // for a run that recovers on the next poll. What it means for a reader:
  // `armedExportRunRef` / `runContactIdsRef` / `hookSpokeRef` are reset ONLY by
  // an observed terminal state, and a page that never sees one keeps them until
  // unmount. Covered by `ImportDetailPage.notice.test.tsx`'s error-path test
  // (/pr-review iter 1, Agent C I2).
  // NO `biome-ignore` here, and that is checked rather than assumed. Review
  // asked for one (/pr-review iter 1, Agent A 3(a)) on the grounds that the
  // omission reads as unattributed next to the annotated suppression at line
  // ~324. Adding it makes `biome lint` emit `suppressions/unused` — Biome's
  // `useExhaustiveDependencies` does not flag this effect, so the comment would
  // suppress nothing and become one more thing to keep true. The prose above is
  // the attribution; the annotation would be noise the linter itself rejects.
  useEffect(() => {
    if (exportInProgress) {
      // Did this page instance watch the run START, or join it already running?
      // Only the first case can name the run's contacts: the snapshot below
      // collects rows while they are `pending`, and a row that finished before
      // the page first looked is indistinguishable from one an EARLIER run
      // exported. Joining mid-run (a reload, a second tab) therefore yields a
      // partial set, and reporting a count from it under-states the run —
      // measured: joining with 1 of 2 already done announced "1 contact".
      if (!armedExportRunRef.current) sawRunStartRef.current = wasIdleRef.current;
      armedExportRunRef.current = true;
      hookSpokeRef.current = false;
      // Widen, never narrow: later polls reveal contacts the first poll had not
      // started yet, so ids are only ever added.
      const seen = runContactIdsRef.current ?? new Set<string>();
      for (const r of exportResults) {
        if (r.export_status === "pending") seen.add(r.contact_id);
      }
      runContactIdsRef.current = seen;
      return;
    }
    // We are watching, we HAVE an answer, and it says nothing is running: so
    // whatever starts next, we will have seen begin. The `exportStatus` check
    // is what makes this true — the first render has no data yet, and treating
    // "not loaded" as "idle" made a page that mounts mid-run believe it had
    // watched the run start.
    if (exportStatus !== undefined) wasIdleRef.current = true;
    if (!armedExportRunRef.current) return;
    armedExportRunRef.current = false;
    const runIds = runContactIdsRef.current;
    runContactIdsRef.current = null;
    // Every ref in this cluster is reset here, `sawRunStartRef` included, so
    // one place owns the whole lifecycle. Today the arming branch reassigns it
    // from `wasIdleRef` before it is ever read again, which makes this reset
    // belt-and-braces rather than a behaviour change — deliberately kept so a
    // future arming path that stops reassigning cannot inherit run N-1's trust
    // (/pr-review iter 1, Agent A 2(a); pinned by the two-run tests in
    // `ImportDetailPage.notice.test.tsx`).
    const sawRunStart = sawRunStartRef.current;
    sawRunStartRef.current = false;
    // The hook already told them; two toasts for one run is worse than none.
    if (hookSpokeRef.current) return;
    const mine = runIds ? exportResults.filter((r) => runIds.has(r.contact_id)) : exportResults;
    const failed = mine.filter((r) => r.export_status === "failed").length;
    const exported = mine.filter((r) => r.export_status === "exported").length;
    // Rows this run SENT and did not WRITE — the undecided conflicts. The
    // seventh surface where one number was answering two questions, and the
    // last one still doing it: a run whose rows are all undecided conflicts is
    // the state this whole card fought to keep RUNNABLE, and it toasted
    // "Added 0 contacts to the search." as a success with no mention of what
    // did happen, while the panel three lines away named it correctly
    // (/pr-review iter 2, Agent C N3).
    //
    // Scoped to `mine` for the same reason `failed` is: a row already terminal
    // when the page joined could belong to any earlier run.
    const skipped = mine.filter((r) => r.export_status === "skipped").length;
    // Both halves, when a run is partly each — the previous version returned
    // early on any failure and never mentioned what DID land.
    // A count we cannot stand behind is worse than no count: when the page
    // joined a run already in progress the snapshot is partial, so the notice
    // says the run finished without claiming a number.
    if (!sawRunStart) {
      // Partial snapshot: no NUMBER is defensible, and that includes the
      // failure count. Reporting "0 failed" from a set we know is incomplete
      // would route a partly-failed run down the success wording — the same
      // mistake in the other direction, which is why the distrust is checked
      // before the branch rather than inside each arm.
      //
      // The SEVERITY is a separate question from the count, and the first
      // version of this branch conflated them: it fired `toast.success` even
      // when the rows we CAN see already carry failures, handing a recruiter a
      // green tick for a provably mixed run (/pr-review iter 1, Agent A 2(b)).
      // A failure inside the run's OWN partial snapshot is a fact; only its
      // total is unknown. So an incomplete set with such a failure speaks as an
      // error WITHOUT a number, and one with none speaks as `info` — we
      // finished, we cannot say how much (never `success`, which would assert
      // an outcome we do not have).
      //
      // `failed` is read off `mine`, NOT off the whole payload, and that is the
      // difference between this being a fix and being the stale-failure bug
      // again: a row already terminal when the page joined might belong to any
      // earlier run, and counting it would make one old failed contact
      // announce a failure for every run this import ever has. A row that was
      // `pending` at join and `failed` at terminal is ours beyond doubt, which
      // is the only failure this branch can honestly report.
      // FLAGGED ROWS ARE NAMED HERE TOO. These two arms deliberately refuse to
      // quote a COUNT — the page joined mid-run, so its snapshot is partial and
      // any number would be a claim it cannot support. That reasoning is about
      // the count, not about the fact: a row that was `pending` when the page
      // joined and `skipped` at terminal is ours beyond doubt, exactly as the
      // comment above argues for `failed`. Leaving them out made these the only
      // outcome-reporting surfaces with no mention of rows the recruiter has to
      // decide (/pr-review iter 4, Agent C N3).
      const joinedNotAdded = exportNotAddedClause(skipped);
      // "THE RUN FINISHED", NOT "ADDED TO THE SEARCH". These arms exist because
      // the page joined mid-run and its snapshot cannot support a count — and
      // `addAction.pastTense` is not a count, it is a CLAIM: "Added to the
      // search". Probed by a reviewer on a run that came back all-skipped and
      // it said exactly that, having added nobody (/pr-review iter 4, Agent A
      // M1).
      //
      // The panel's copy for this same unknown state already refuses the verb
      // (`EXPORT_RESULT_UNKNOWN`: "The run finished."). These three surfaces are
      // the ones that kept it, one level out from the four arms the shared
      // clause covers — which is where the seventh instance of this defect was
      // hiding.
      if (failed > 0) {
        toast.error(
          `The run finished. Some contacts failed, so reopen the contacts table to check.${joinedNotAdded}`
        );
      } else {
        toast.info(
          `The run finished. Open the contacts table to see the results.${joinedNotAdded}`
        );
      }
      return;
    }
    if (failed > 0) {
      const added = exported > 0 ? `${addAction.completion(exported)} ` : "";
      // `flagged` is a variable rather than a concatenation because a run can
      // fail SOME rows and leave others unwritten — naming only the failures is
      // the same omission, one cell over. "Flagged To review" was the old
      // wording here and it no longer holds: since 2026-09-17 a skipped row may
      // simply never have been compared (/pr-review iter 6, Agent C m5).
      const notAdded = exportNotAddedClause(skipped);
      toast.error(
        `${added}${failed} contact${failed === 1 ? "" : "s"} failed. Reopen the contacts table to retry.${notAdded}`
      );
      return;
    }
    if (exported === 0 && skipped > 0) {
      // Wrote nothing, and that is not a failure — see `exportNothingWrittenLine`.
      // Nothing was written, and that is not a failure — the rows went, the
      // worker resolved each open conflict to a skip, and they come back
      // flagged To review. "Added 0 contacts" as a success is true and useless;
      // `info` with the outcome is what the recruiter can act on. NOT the word
      // `skipped`, which is what the table stopped displaying after #748.
      toast.info(exportNothingWrittenLine(skipped));
      return;
    }
    // The mixed run: contacts landed AND some came back flagged. The success
    // stands; the half the recruiter has to act on is not swallowed.
    toast.success(addAction.completion(exported) + exportNotAddedClause(skipped));
  }, [exportInProgress, exportResults, exportStatus]);

  const contactsQuery = useQuery({
    queryKey: ["import-contacts", importId, page, contactFiltersKey],
    // The payload carries the MEMBERSHIP key it was fetched for.
    //
    // `placeholderData` serves the previous result while a refetch is in
    // flight, and react-query does not hand back the key that produced it — so
    // "are these counts for the filters currently on screen?" is otherwise
    // unanswerable. `isPlaceholderData` answers a WIDER question and answering
    // it was over-broad: the key carries `page` and the sort, neither of which
    // changes what a write would cover, so both Add CTAs went dead for a round
    // trip on every page turn and every sort toggle (/pr-review iter 2,
    // Agent A N3 / Agent C N2).
    queryFn: async () => {
      const res = await api.listContacts(importId, {
        ...contactFilters,
        page,
        page_size: pageSize,
      });
      return { ...res, _selectionFiltersKey: selectionFiltersKey };
    },
    enabled: Boolean(importId),
    // Keep the previous page on screen while a filter/page change refetches —
    // otherwise every debounce settle would blank the table for a frame.
    placeholderData: keepPreviousData,
    // Contacts are written async (parse by import_worker, export_status by
    // export_worker). Poll while parsing OR while an export is in flight so the
    // table shows live 0→N contacts and live imported→exported per row; stop
    // once both are terminal.
    refetchInterval: () => (parseStatus === "parsing" || exportInProgress ? 1500 : false),
  });

  // Guarantee one fetch the instant parsing settles → ready: the poll above
  // stops scheduling when `parseStatus` flips, so without this the table could
  // miss the rows written in the last ~2s before the status transition.
  const lastParseStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastParseStatus.current === "parsing" && parseStatus === "ready") {
      void contactsQuery.refetch();
    }
    lastParseStatus.current = parseStatus;
  }, [parseStatus, contactsQuery]);

  // When an export settles, refetch once: contacts (final per-row state) + detail
  // (exported_count / `exported` tag). The export poll stops scheduling on the
  // flip, so this lands the last update.
  const lastExportStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const s = exportStatus?.status;
    const wasRunning =
      lastExportStatus.current === "queued" || lastExportStatus.current === "running";
    if (wasRunning && (s === "succeeded" || s === "failed")) {
      void contactsQuery.refetch();
      void detailQuery.refetch();
    }
    lastExportStatus.current = s;
  }, [exportStatus, contactsQuery, detailQuery]);

  // Selection model (sub-task 05): explicit ids, or "all N matching the
  // active filters" as a filter-scoped claim (never a materialized id list).
  // The hook owns the mode machine + the stale-N invalidation; this page only
  // feeds it the universe key and the live filtered total. In all-filtered
  // mode `selection.selectedIds` is EMPTY by design — every consumer below
  // that reads an empty explicit selection (Compare scope, the narrowing
  // fetch) already treats it as whole-import semantics.
  const selection = useContactsSelection({
    filtersKey: selectionFiltersKey,
    total: contactsQuery.data?.total,
  });
  const selectedIds = selection.selectedIds;
  const inAllFilteredMode = selection.mode === "all-filtered";
  // The N every cross-page surface quotes: the live server total for the
  // active filters. The hook clears the mode if this drifts from the total
  // captured at escalation, so a rendered N is never stale.
  const filteredTotal = contactsQuery.data?.total;

  const detail = detailQuery.data;

  // TWO Company Map discriminators, because the backend has two and they are
  // documented as orthogonal (`schemas.py:28-53`; both are client-declarable,
  // so a row CAN be `source="bct", import_format="baby_chops_v1"` — that row
  // is this repo's canonical divergent fixture, `imports.fixtures.ts:279`).
  //
  // Collapsing them into one name was wrong and /pr-review iter 2 (Agent C,
  // Important 1) caught it. Which axis a consumer needs depends on what it is
  // asking about:
  //
  //   BEHAVIOUR — "will this write create a candidacy?" -> `source`.
  //     `export_worker/service.py` gates candidacy creation on `source`, so
  //     the write CTA and the compare copy that describes the write must read
  //     the same column that produces the behaviour they promise.
  //
  //   SCHEMA — "does this row carry bct_* fields?" -> `import_format`.
  //     `service.py:847` computes `is_bct = header["import_format"] == "bct"`
  //     and `domain.py:815` SILENTLY DROPS every `bct_*` filter param when it
  //     is false. Gating the filter fieldset or the annex columns on `source`
  //     would render controls that post params the server discards without an
  //     error — the recruiter types a company, presses enter, and gets the
  //     unfiltered list back with no feedback.
  //
  // `schemas.py` records a prior incident of exactly this shape (PR #639, I5:
  // "the D5 no-chaining guard keys on `import_format` server-side while the
  // picker keys on `source`"). Two names is the cheapest way not to repeat it.
  const isBctSource = detail?.source === "bct";
  const isBctFormat = detail?.import_format === "bct";

  // The write action's caveat lives behind an ⓘ but must stay in the
  // accessibility tree on the BUTTON — see `SearchCard`'s note on the review
  // round that rejected a hover-only version.
  const addActionNoteId = useId();

  const contacts = contactsQuery.data;
  const columns = contacts?.columns ?? [];

  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds]);

  // Compare's "pending" denominator is BROADER than Export's `exportable_remaining`
  // (which excludes W1 duplicates): Compare must keep re-surfacing W1 duplicates on
  // every run until they're resolved in Thrive. So both the divider and the
  // Compare-button gate use `total_contacts - exported_count`.
  const totalContacts = detail?.total_contacts ?? 0;
  const exportedCount = exportStatus?.exported_count ?? detail?.exported_count ?? 0;

  // The selection the CONFIRMED run scope uses: choosing "Compare all X" in
  // the pre-compare confirmation empties it for scope purposes only (fu4
  // sub-task 13) — `selectedList` and the table selection stay as they are.
  const compareSelectedList = useMemo(
    () => (compareAll ? [] : selectedList),
    [compareAll, selectedList]
  );

  // Per-contact `export_status` accumulator. `selectedIds` persists across
  // pagination, but the per-contact `export_status` only rides on the
  // currently-loaded page's query data — so we accumulate it as pages load to
  // classify a cross-page selection without a new batch-lookup endpoint.
  const exportStatusByIdRef = useRef<Map<string, string | null>>(new Map());
  useEffect(() => {
    const items = contactsQuery.data?.items;
    if (!items) return;
    for (const c of items) {
      exportStatusByIdRef.current.set(c.import_contact_id, c.export_status ?? null);
    }
  }, [contactsQuery.data]);

  // Dismissed-contact accumulator, filled from the same pages. STATE, not a
  // ref like its sibling above, and that difference is load-bearing: leave-out
  // is UNDOABLE, so this set both grows and shrinks, and the pre-compare
  // confirmation's selection arm has to re-derive its number when it does. A
  // ref would keep serving the count from before the Undo until some unrelated
  // dependency happened to move.
  const [ignoredIds, setIgnoredIds] = useState<ReadonlySet<string>>(() => new Set());
  const absorbIgnored = useCallback((items: readonly ImportContactRow[]) => {
    setIgnoredIds((prev) => {
      const next = new Set(prev);
      for (const c of items) {
        if (c.left_out) next.add(c.import_contact_id);
        else next.delete(c.import_contact_id);
      }
      // Same-content updates must not produce a new identity — this runs on
      // every contacts refetch (including the export panel's poll).
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, []);
  useEffect(() => {
    const items = contactsQuery.data?.items;
    if (items) absorbIgnored(items);
  }, [contactsQuery.data, absorbIgnored]);

  // A whole-import Compare on a partially-exported import USED to fetch the
  // entire contact list here — six sequential requests on a 516-row import,
  // every time the modal opened — purely so the client could filter the exported
  // rows out and post the surviving ids. Both halves of that are gone: the
  // exclusion is a clause in the compare scope query, and the whole-import shape
  // posts no ids at all. See the `compareScope` memo below, and
  // `.agent/Tasks/2026-09-12-compare-body-over-waf-limit/`.
  //
  // The accumulators (`exportStatusByIdRef`, `ignoredIds`) are still filled from
  // the paged `contactsQuery` above, which is where they were always filled
  // from; the narrowing fetch only ever topped them up with pages the recruiter
  // had not visited, and nothing reads them for rows that are not on screen.

  // The import's latest APPLIED AI Review run. Two readers, and they want it at
  // different moments, which is why the gate is the OR:
  //
  //  * `rationaleFor` (below) — per-cell rationale for the annex columns, which
  //    only exist when `isAiReviewed`.
  //  * the criteria card — the criteria this file was scored against
  //    (Trello #711 cl.5), which a review file should state whether or not its
  //    score columns have resolved.
  //
  // Null for never-applied imports: the endpoint 404s, cells show just the
  // badge, and the card does not render.
  // The arriving run, polled directly by id. `useAiReviewRun` already owns the
  // cadence, the terminal-status rules and the stall deadline — reused rather
  // than re-derived, which is the mistake `/pr-review` caught three times on
  // this branch.
  const {
    run: arrivingRun,
    stalled: arrivingRunStalled,
    pollFailed: arrivingRunPollFailed,
  } = useAiReviewRun({
    client: aiReviewApi,
    reviewId: isAiReviewFormat ? arrivedWithRunId : null,
  });

  // Does the `?run=` in the URL belong to THIS import? Only partly answerable,
  // and the honest scope is written here because the first version of this
  // comment claimed more than the code did (`/pr-review` round 8, §2a).
  //
  // WHAT IT CATCHES: a run that has finished and produced somebody else's file.
  // That is the only case decidable from `AiReviewResult`, whose
  // `output_import_id` is narrowed server-side to a `ready` child
  // (`ai_reviews/service.py` — "None for every pre-finished state and for
  // legacy in-place runs"). Its practical effect is that the `applied`
  // invalidation below cannot be triggered for a foreign run.
  //
  // WHAT IT DOES NOT CATCH, stated because it is exactly what the finding that
  // prompted the guard was about: a foreign run that is still IN FLIGHT, or one
  // that FAILED. Both carry a null `output_import_id` — permanently, in the
  // failed case — so both read as ours, and `/imports/<A>?run=<run-of-B>` will
  // show B's progress or B's failure text on A's page.
  //
  // The behaviour is visible in "says WHY when the run failed, instead of an
  // empty table" — that test does not know its run is foreign, and cannot: the
  // schema carries no source-import id, which is the same reason the gap
  // exists. A test asserting the residual would be that test with a different
  // name, so there is not one; this comment is the record.
  //
  // ACCEPTED rather than fixed, and the reasoning is the scope of the harm:
  // nothing here is authorization. `GET /ai-reviews/{id}` is not scoped by
  // caller at all (pre-existing, unchanged by this PR), so anyone holding a run
  // id already reads that run directly; this only decides whose page renders
  // it. Closing it properly needs the run's SOURCE import id on
  // `AiReviewResult`, which the schema does not carry — a wire change, and not
  // one this card asked for. Recorded as a follow-up instead of pretended away.
  const arrivingRunIsOurs =
    arrivingRun?.output_import_id == null || arrivingRun.output_import_id === importId;

  // The deadline is READ here, and that is the whole point of destructuring
  // three fields instead of one.
  //
  // `useAiReviewRun` arms a 15-minute stall deadline for every caller. While
  // the creator waited for `applied`, IT was the reader; the 202-navigate moved
  // the waiting to this page and the reading did not come with it
  // (`/pr-review` iter 6, Agent C I1). A run can sit non-terminal forever by
  // DESIGN — a failed apply hand-off parks it back on `succeeded` for operator
  // recovery — and the sweeper settles the derived FILE on a three-hour cadence,
  // not the run. So without this the recruiter watches "Scoring candidates…"
  // for three hours and then gets a bare Failed pill with no reason.
  //
  // This is iter 1's Critical, relocated: the bug was never that the deadline
  // was missing, it was that the page doing the waiting was not the page
  // holding the machinery.
  //
  // No preview arm, unlike the creator's banner: a preview writes no file and
  // therefore never navigates here. Every run this page sees is a full one.
  const arrivingRunAbandoned =
    arrivingRunIsOurs &&
    (arrivingRunStalled || arrivingRunPollFailed) &&
    arrivingRun?.status !== "applied" &&
    arrivingRun?.status !== "failed";

  // Scoring, not parsing. `isRunInFlight` is the shared predicate — this page
  // is its fourth reader and deliberately does not re-derive it.
  const aiReviewInFlight = arrivingRunIsOurs && isRunInFlight(arrivingRun);
  // SCORING and SAVING are different waits, and conflating them put the wrong
  // sentence on the longer one (round 8, Agent C I1).
  //
  // `isRunInFlight` is true through `succeeded` (for a full run) and
  // `applying` — both of which mean Bedrock is DONE and the worker is building
  // the file. Rendering "Scoring candidates against your criteria" there
  // restates work already finished, and since the API emits `progress` only
  // from `processing`, the "— N of M" counter vanishes at the same moment, so
  // the sentence freezes too. The apply hop is also exactly where a build
  // STRANDS: a DLQ-exhausted apply sits in `applying` until the sweeper settles
  // the file three hours later, so the wrong copy was showing for the longest
  // and most confusing wait this feature has.
  //
  // `ScoresTable` already made this distinction on the creator page — its
  // `autoApplyPending` picks "Saving…" over "Scoring…" under a comment saying
  // the latter "would restate work already done". The reasoning did not travel
  // to the page that inherited the waiting. Same shape as the stall-deadline
  // finding this branch already fixed once.
  const aiReviewSaving =
    aiReviewInFlight && (arrivingRun?.status === "applying" || arrivingRun?.status === "succeeded");
  const aiReviewFailureMessage =
    arrivingRunIsOurs && arrivingRun?.status === "failed"
      ? aiReviewFailureText(arrivingRun.error)
      : null;
  // Scoring is paused because the AI provider is down, and the worker will
  // retry on its own (2026-09-29). Still `processing` and still in flight —
  // this changes what the line SAYS, not whether the page waits. Without it
  // the counter just froze, and a frozen counter is what makes people retry.
  const aiReviewWaitingMessage =
    aiReviewInFlight && arrivingRun?.status === "processing"
      ? aiReviewWaitingText(arrivingRun.error)
      : null;

  // When the run lands, the criteria card and the per-cell rationales become
  // available — but `useLatestAiReview` has no `refetchInterval` and nothing
  // else invalidates it, so without this they would not appear until a manual
  // reload. That is the trap that makes a naive navigate-on-202 WORSE than
  // waiting: the recruiter arrives early and the page never finishes.
  const seenAppliedRef = useRef(false);
  useEffect(() => {
    if (!arrivingRunIsOurs || arrivingRun?.status !== "applied" || seenAppliedRef.current) return;
    seenAppliedRef.current = true;
    void pageQueryClient.invalidateQueries({
      queryKey: ["ai-review", "by-import", importId],
    });
  }, [arrivingRun?.status, arrivingRunIsOurs, pageQueryClient, importId]);

  const reviewQuery = useLatestAiReview({
    client: aiReviewApi,
    importId,
    enabled: isAiReviewed || isAiReviewFormat,
  });
  // The Bullseye shortcut: an AI Review list whose run is APPLIED to a linked
  // search is a slice of that search's Bullseye, so it opens there alone
  // (`?review=` switches the other lists off). Same page gate as the tab.
  const bullseyeVisible = pageVisible(
    activeFlags(),
    usePreviewModeIfMounted(),
    "client-engagement",
    BULLSEYE_PAGE_ID
  );
  const appliedReviewId = reviewQuery.data?.status === "applied" ? reviewQuery.data.job_id : null;
  const bullseyeSearchId = detailQuery.data?.search_id_thrive ?? null;
  const bullseyeHref =
    bullseyeVisible && appliedReviewId !== null && bullseyeSearchId
      ? `${searchDetailPath(bullseyeSearchId)}?tab=bullseye&review=${encodeURIComponent(appliedReviewId)}`
      : null;
  const rationaleFor = useMemo(() => {
    const review = reviewQuery.data;
    if (!review?.items) return undefined;
    const byContact = new Map<string, Map<string, string>>();
    for (const item of review.items) {
      const byCriterion = new Map<string, string>();
      for (const s of item.scores) byCriterion.set(s.criterion_name, s.rationale);
      byContact.set(item.import_contact_id, byCriterion);
    }
    return (contactId: string, criterionKey: string) => byContact.get(contactId)?.get(criterionKey);
  }, [reviewQuery.data]);
  // `description` first, `name` as fallback: the creator writes the typed sentence
  // into both fields, so either round-trips for a run this app made — but a run
  // written by any other client may have set only `name`.
  const reviewCriteria = useMemo<Criterion[]>(
    () =>
      (reviewQuery.data?.criteria ?? []).map((c, i) => ({
        id: `review-criterion-${i}`,
        text: c.description || c.name,
      })),
    [reviewQuery.data]
  );

  // SINGLE source of truth for what the table shows — consumed BOTH by the render
  // below AND by `useResolveAvatars`, so the presign batch always matches exactly
  // the rows on screen. Sort + pagination are fully server-side.
  const displayItems = contactsQuery.data?.items ?? [];
  // Resolve the on-screen rows' stored S3 avatars in one (client-chunked) batch so
  // the table renders the durable copy. A row with no stored key simply shows
  // initials — the web has no licdn fallback.
  const resolvedAvatars = useResolveAvatars(api, displayItems);
  // A list pulled from a Thrive search (04), or derived from one with its
  // candidacy data (05), shows the Candidates tab's photos for the rows with
  // no stored LinkedIn avatar, by the Thrive contact each was pulled from
  // (`thrive_source_contact_id` — Compare can null `contact_id_thrive`, never
  // this one): enqueue the missing ones, poll until they land. The photo is
  // chosen PER ROW (`rowAvatarUrl`): a row with a LinkedIn avatar keeps it, so
  // ChopLink rows of a combined list, and people in both kinds of list, keep
  // their LinkedIn photo. Every other list asks nothing here.
  const isThriveSourced =
    detailQuery.data?.import_format === "thrive_search_v1" ||
    detailQuery.data?.has_candidacy_data === true;
  const avatarsApi = useMemo(
    () => avatarsClient ?? createAvatarsClient({ apiBase: import.meta.env.VITE_API_URL ?? "" }),
    [avatarsClient]
  );
  const thriveAvatarRows = useMemo(
    () =>
      isThriveSourced
        ? displayItems
            .filter((row) => !row.profile_image_s3_key)
            .map((row) => ({
              contact_id: row.thrive_source_contact_id ?? null,
              linkedin_url: row.linkedin_url ?? null,
            }))
        : [],
    [isThriveSourced, displayItems]
  );
  const thriveAvatars = useResolveThriveAvatars(
    isThriveSourced ? avatarsApi : undefined,
    thriveAvatarRows
  );

  function handleSort(key: string) {
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : // First click: names default A→Z (asc); score columns default
          // highest-first (desc). Toggles thereafter.
          { key, dir: key === NAME_SORT_KEY ? "asc" : "desc" }
    );
    setPage(1);
  }

  // Compare's contact scope. Already-exported and dismissed contacts are out of
  // it (pending / skipped / failed / never-attempted stay in — they still need
  // action), and BOTH exclusions are now applied SERVER-SIDE, in the compare
  // scope query. See `computeCompareScope` for the whole-import vs. selection
  // rules that remain the client's business: what to COUNT, not what to run.
  //
  // A whole-import Compare on a partially-exported import used to take a
  // narrowing branch here: fetch the entire contact list (six requests on a
  // 516-row import), filter the exported rows out client-side, and post the
  // surviving ids. That produced a ~20 KB request body, and the shared ALB's WAF
  // refuses any body over 8192 bytes with a bare `403` carrying no CORS headers
  // — which the browser can only surface as `TypeError: Failed to fetch`. The
  // branch, its query, and its `preparingScope` / `narrowingError` states are
  // gone with it. See `.agent/Tasks/2026-09-12-compare-body-over-waf-limit/`.
  //
  // WHY THE OLD COMMENT'S WARNING NO LONGER APPLIES — worth stating, because
  // that comment is in the diff right next to this one. It said that taking
  // `alreadyExportedCount` / `scopeTotal` from the export aggregate "would
  // reintroduce the very aggregate-vs-list mismatch this narrowing was written
  // to eliminate", and it was right while the CLIENT chose the rows: the numbers
  // had to come from the same list the run received, or the modal would
  // advertise a scope the run did not honour. Now the SERVER chooses the rows,
  // by a rule that is always true, so there is no list to agree with — the
  // aggregate is not a rival account of the scope, it is a count of it. The
  // mismatch is resolved in the other direction: instead of bending the number
  // to match the run, the run now matches what "still pending" always meant.
  const compareScope = useMemo(
    () =>
      computeCompareScope({
        selectedIds: compareSelectedList,
        exportStatusById: exportStatusByIdRef.current,
        ignoredIds,
        totalContacts,
        exportedCount,
      }),
    // `exportStatusByIdRef` is a monotonically-filled ref read at compute time,
    // so the scalars are the meaningful deps. `ignoredIds` IS a dep (state, not
    // a ref): undoing a dismissal has to put the row back in scope without
    // waiting for an unrelated dependency.
    [compareSelectedList, exportedCount, totalContacts, ignoredIds]
  );

  // How many contacts the Compare run will actually cover — the number the
  // modal's "(X pending)" suffix promises. `undefined` when this page cannot
  // answer exactly, which leaves the modal on its over-stating fallback rather
  // than on a confidently wrong number.
  //
  // Computed HERE and handed over whole, rather than shipping two counts for
  // the modal to subtract, because the two counts come from different places:
  // `exportedCount` is the denormalized export aggregate, while
  // `counts.review.ignored` belongs to the `review_status` partition where
  // `ignored` OUTRANKS `exported`. A contact exported and later dismissed sits
  // in both, so subtracting them separately drops it twice. Taking BOTH
  // SUBTRAHENDS from the same partition is what makes the arithmetic valid —
  // `compareScopeFromReviewCounts`' docstring spells out that disjointness and
  // this is the same reasoning applied one screen over.
  //
  // The MINUEND is not from that partition: `totalContacts` is
  // `imports.total_contacts`, a denormalized column read by a different query.
  // It is the right number — the partition's buckets cover the whole import —
  // but it is a second producer for one value, a shape this repo has been bitten
  // by before. If the two ever disagree the `Math.max(0, …)` keeps the count
  // low rather than negative.
  //
  // Under a display filter `counts.review` answers about the FILTERED set, so
  // it cannot answer a whole-import question: we return `undefined` instead.
  // A selection carries its own exact count from `computeCompareScope`, which
  // classifies the ids it was handed.
  const comparePendingOverride = useMemo((): number | undefined => {
    if (compareScope.scope === "selection") return comparePendingCount(compareScope);
    if (hasNarrowingFilters) return undefined;
    const review = contacts?.counts?.review;
    if (!review) return undefined;
    return Math.max(0, totalContacts - review.exported - review.ignored);
  }, [compareScope, hasNarrowingFilters, contacts?.counts?.review, totalContacts]);

  // The numbers the pre-compare confirmation DISPLAYS — produced by the same
  // `computeCompareScope` the confirmed run scope goes through, then read
  // through `comparePendingCount`, so the modal can never advertise a count the
  // enqueued run won't honour (fu4 sub-task 13; pinned by the interposition spy
  // test). `null` = no active selection → the confirmation renders the single
  // "all" statement, no radio group.
  //
  // The "all" number MUST go through `comparePendingCount` too: it used to read
  // `scopeTotal`, which is the whole import, while a whole-import run on a
  // partially-exported import takes the narrowing branch above and compares
  // only the not-yet-exported contacts. 100 contacts with 40 exported displayed
  // "all 100" and compared 60. The invariant now holds by construction, not by
  // this comment.
  const preCompareSelectedScope = useMemo(() => {
    if (selectedList.length === 0) return null;
    const scope = computeCompareScope({
      selectedIds: selectedList,
      exportStatusById: exportStatusByIdRef.current,
      ignoredIds,
      totalContacts,
      exportedCount,
    });
    return {
      pending: comparePendingCount(scope),
      alreadyExported: scope.alreadyExportedCount,
      ignored: scope.ignoredCount,
    };
  }, [selectedList, totalContacts, exportedCount, ignoredIds]);
  // The whole-import arm as the PAGE can see it: net of the export narrowing,
  // blind to dismissals (there is no whole-import dismissal aggregate — the
  // accumulator only knows the pages that have loaded). The confirmation shows
  // this until its own unfiltered `counts.review` fetch lands and supplies the
  // exact breakdown; see `compareScopeFromReviewCounts`.
  const preCompareAllScope = useMemo(() => {
    const scope = computeCompareScope({
      selectedIds: [],
      exportStatusById: exportStatusByIdRef.current,
      ignoredIds,
      totalContacts,
      exportedCount,
    });
    return {
      pending: comparePendingCount(scope),
      alreadyExported: scope.alreadyExportedCount,
      ignored: scope.ignoredCount,
    };
  }, [totalContacts, exportedCount, ignoredIds]);

  // Whether the "Compare to Thrive" button goes straight to the run. Derived
  // from the SAME two scopes the confirmation displays, so the skip and the
  // modal's collapsed state can never disagree about what the run covers.
  const skipPreCompareConfirmation = shouldSkipPreCompareConfirmation({
    totalContacts,
    pageSize,
    // Derived from the SAME object the table query sends, so a new filter is
    // covered the day it is added rather than the day someone remembers this
    // line. Sort keys are excluded: ordering narrows nothing.
    hasActiveFilter: Object.entries(contactFilters).some(
      ([k, v]) => v !== undefined && v !== "" && k !== "sort_key" && k !== "sort_dir"
    ),
    selectedScope: preCompareSelectedScope,
    allScope: preCompareAllScope,
  });

  /** A compare run that SUCCEEDED for this list. `compared_at` is null in the
   *  three states that are not that — never run, in flight, failed — which is
   *  the one bit the add-to-search CTA needs. Shared with `ContactsTable`,
   *  which asks the same question of the same field. */
  /** A compare THIS SESSION watched finish, before `compared_at` catches up. */
  const [compareSucceededHere, setCompareSucceededHere] = useState(false);
  /**
   * The buckets of the compare run THIS SESSION watched succeed (epic
   * 2026-09-22, sub-task 05) — feeds `AlreadyInSearchBanner`'s count. `null`
   * both before any run has succeeded and once "Remove them" has cleared the
   * bucket (the banner's own `onRemoved`), so it doubles as "is there a
   * banner to show" without a second flag.
   *
   * Captured from a WHOLE-IMPORT Compare that succeeds — manual or
   * auto-triggered, either scopes to "import" when nothing is selected — but
   * NOT from a selection-scoped run (iter-1 review, finding C-I4): the "N
   * already in search — Remove them" bulk action flips the whole
   * `compare_bucket`, so a selection-scoped run's own count could not honour
   * what the button actually does. The page renders the captured buckets
   * into a banner ONLY for `detail?.import_format === "combined"` (below),
   * so a manual whole-import Compare on any other import type is unaffected.
   * Gating on the DATA here rather than on the FLOW that started the run
   * would have to be duplicated at every future caller of the same
   * `CompareModalHost`; gating the one render site instead keeps it in one
   * place.
   */
  const [compareBuckets, setCompareBuckets] = useState<CompareBuckets | null>(null);
  /**
   * A combined list arrives with the Thrive results it INHERITED from its
   * sources, and no Compare runs on arrival (epic
   * 2026-10-01-combine-stability-perf, sub-task 01). The stored result is
   * read once (a database read, no Thrive call) so the "N already in search"
   * banner shows the inherited counts, as it did after the old automatic
   * Compare. Seeded once per mount, so "Remove them" clearing the banner
   * is not undone by a refetch.
   *
   * The count is `inherited.in_search_remaining`, read LIVE by the server
   * (in-search rows not left out), never the stored `buckets`: those do not
   * move when "Remove them" leaves the rows out, so the banner came back on
   * every later visit (/pr-review iter 5 on PR #993, Agent A N10). Seeded
   * only from a settled read (not while a refetch is in flight), so a
   * remount does not seed from a cached answer older than the removal.
   */
  const inheritedCompareQuery = useQuery({
    queryKey: ["imports", importId, "compare", "inherited"],
    queryFn: () => api.getCompare(importId),
    enabled:
      detail?.import_format === "combined" &&
      detail?.status === "ready" &&
      detail?.compared_at != null,
    refetchOnMount: "always",
  });
  const seededInheritedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (inheritedCompareQuery.isFetching) return;
    const result = inheritedCompareQuery.data;
    if (!result?.inherited || result.status !== "succeeded") return;
    if (seededInheritedForRef.current === importId) return;
    seededInheritedForRef.current = importId;
    // The banner reads only `inSearchCount(buckets)`: carry the live count
    // there, the rest of the stored buckets unchanged.
    setCompareBuckets({
      new: result.buckets?.new ?? 0,
      existing_add_to_search: result.buckets?.existing_add_to_search ?? 0,
      already_in_search: result.inherited.in_search_remaining,
      linkedin_position_update: 0,
    });
  }, [inheritedCompareQuery.data, inheritedCompareQuery.isFetching, importId]);
  /** The scope dialog that gates every Add. */
  const [preAddOpen, setPreAddOpen] = useState(false);
  /** Which arm the recruiter picked in it. `"all"` until they say otherwise —
   *  the no-selection case, which is what the Search-card CTA always is. */
  const [addScopeChoice, setAddScopeChoice] = useState<PreAddChoice>("all");
  const compareHasRun = detail?.compared_at != null || compareSucceededHere;

  /**
   * Is there a SCOPE QUESTION to ask, or only a scope to state?
   *
   * The dialog used to gate every route into Add, on a "one door" argument.
   * The product owner overruled it on 2026-09-16 — *"se eu selecionei todos os
   * usuarios, ou so alguns, a tela nao deve aparecer... a tela deve ser bem
   * limpa"* — and the argument does not survive contact with what the other
   * three routes actually show:
   *
   *   * no selection     — the dialog stated "Add all N in this list", and the
   *                        panel behind it states the same thing again.
   *   * some rows ticked — there is nothing to choose; the recruiter named the
   *                        rows.
   *   * the banner       — "Select all 120 contacts" IS the whole-import
   *                        choice, already made, in the table.
   *
   * What remains is the one case report 2 was actually about: the page is
   * fully ticked and more contacts exist beyond it, so "these 100" and "all
   * 120" are different runs and the recruiter has not yet said which.
   *
   * The dialog is still the only place that asks. It is no longer the only
   * place that acts, so the two things the dialog also did — refusing an
   * over-cap selection, and explaining a scope with nothing to send — moved
   * to the panel, which every route does reach.
   */
  const pageSelectableIds = useMemo(
    () => (contacts?.items ?? []).filter(isExportable).map((r) => r.import_contact_id),
    [contacts?.items]
  );
  const addNeedsScopeChoice =
    !inAllFilteredMode &&
    selectedIds.size > 0 &&
    pageSelectableIds.length > 0 &&
    // Every row this page offers is ticked...
    pageSelectableIds.every((id) => selectedIds.has(id)) &&
    // ...and the list does not end at this page, so "all of them" is a
    // genuinely different run from "these".
    (filteredTotal ?? 0) > (contacts?.items?.length ?? 0);

  function openExportFrom(w2Decisions: Record<string, W2Decision>) {
    setCarriedW2(w2Decisions);
    setCompareOpen(false);
    if (addNeedsScopeChoice) {
      setPreAddOpen(true);
      return;
    }
    // Straight to the panel, with the arm the situation already determines:
    // an explicit selection means those rows, anything else means the scope.
    setAddScopeChoice(!inAllFilteredMode && selectedIds.size > 0 ? "selection" : "all");
    setExportOpen(true);
  }

  // Re-export gating: once the export status reports nothing left to export
  // (every exportable contact is already exported), disable the export entry
  // points.
  //
  // WHILE THE STATUS IS LOADING WE NOW REFUSE, which is the opposite of what
  // this comment said until the same change that made it false. A select-all
  // works out which rows were its own by comparing against the payload as it
  // stood at the click; with no payload there is nothing to compare against and
  // the outcome comes back unreadable. See `exportStatusQuery.isPending` below.
  /**
   * The count and the filters must describe the SAME set, and for one refetch
   * they do not.
   *
   * `placeholderData: keepPreviousData` keeps the previous filters' counts on
   * screen while the new ones load, but `membershipFilters` is derived from
   * state and updates immediately. Opening the dialog inside that window would
   * promise the old filters' number and send the new filters' scope — and in
   * the worst shape (clearing a filter) promise 230 and write 1,300.
   *
   * Gating the CTAs is the honest fix: for the length of one refetch the page
   * does not know what it would write, so it does not offer to.
   */
  const addScopeIsStale =
    contactsQuery.data !== undefined &&
    contactsQuery.data._selectionFiltersKey !== selectionFiltersKey;
  // `undefined` while the first page is still loading, or after a cache
  // eviction — NOT zero. Defaulting to 0 made the dialog refuse with "the
  // contacts that are left have unresolved conflicts", a cause it cannot
  // possibly know at that moment (/pr-review iter 2, Agent C N8). Gating the
  // CTA keeps that state off screen; the `?? 0` below is retained only so the
  // arithmetic is total.
  // Written as "no data", which is what it is: `counts` and `review` are
  // required on the wire and `add_writable` is a non-optional number, so the
  // inner optional chains cannot fire. Spelling it this way is what makes the
  // consequence visible — a FAILED contacts query has no data either, so this
  // gate is also the error state (/pr-review iter 3-4, Agent C M4).
  const addCountsUnknown = contactsQuery.data?.counts?.review?.add_writable === undefined;
  /**
   * Rows the run would DISPATCH, which is not the same question as what it
   * would write — see `addScope`. Also what makes the dialog's zero-writable
   * copy TRUE rather than a guess: `resolvable` already excludes already-added,
   * dismissed and W1-duplicate rows, so `resolvable > 0 && writable === 0`
   * leaves exactly one cause standing, undecided W2/W3. The copy used to assert
   * that cause on an `add_writable === 0` that had four (/pr-review iter 3,
   * Agent C I1).
   */
  const addResolvableCount = contactsQuery.data?.counts?.review?.add_resolvable ?? 0;

  const exportDisabled =
    (exportStatus !== undefined && exportStatus.exportable_remaining === 0) ||
    addScopeIsStale ||
    addCountsUnknown ||
    // THE EXPORT STATUS MUST HAVE LANDED BEFORE A RUN CAN START.
    //
    // Not a new rule so much as the missing half of one. A select-all run works
    // out which rows were its own by comparing against the payload as it stood
    // at the click; with no payload yet there is nothing to compare against,
    // and the run falls back to a weaker signal that cannot see a row settling
    // before the first poll. The result is `export-result-unknown`, which the
    // E2E helper throws on.
    //
    // Every other term here is about whether a run is WORTH starting. This one
    // is about whether its outcome will be readable, and it closes the window
    // rather than adding a second way to cope with it (/pr-review iter 3,
    // Agent C N6).
    // `isPending` OR `isError`. Both mean the same thing for the question this
    // term asks — will the run's outcome be readable? — and only the first was
    // covered: after `getExport` rejects, `isPending` is false, the CTA goes
    // live, and the run starts with no payload to compare against, which is the
    // exact state this gate exists to prevent (/pr-review iter 4, Agent A M2).
    exportStatusQuery.isPending ||
    exportStatusQuery.isError;
  /**
   * What an Add run is about to write, and what it carries on the wire.
   *
   * THE NUMBER. `add_writable` is the server's count of rows the write policy
   * would actually write under the DEFAULT per-contact decision: not already
   * exported, not dismissed, not a W1 duplicate, and not an undecided W2/W3.
   * It is narrowed by the table's active filters, which is what lets the "all N
   * matching your filters" arm be honest. `exportable_remaining` deliberately
   * is NOT used here: it answers "is this import done?" — it gates the CTA and
   * the re-export 409 — and so stays broad enough to keep an import whose only
   * remaining rows are unresolved conflicts actionable.
   *
   * THE SHAPE. `select_all` + the filters, never an id list. `contact_ids` is
   * capped at 200 server-side and ~204 ids cross the ALB WAF's ~8 KB limit as a
   * bare `TypeError: Failed to fetch`, so the explicit lane is structurally
   * incapable of a full-dataset write. Filters ride only when one is active:
   * `w2_decisions` already makes this body's size the thing standing between a
   * select-all and that limit.
   */
  const addWritableCount = contactsQuery.data?.counts?.review?.add_writable ?? 0;
  const addScopeFilters = hasNarrowingFilters ? membershipFilters : null;
  /**
   * TWO NUMBERS, and the `> 0` guard restored.
   *
   * This collapsed to one (`total: addWritableCount`) for one iteration, and it
   * reintroduced the exact defect the PR is about: `selectAllScope.total` is
   * read by three surfaces asking two questions — the confirmation sentence
   * (writes), the submit gate (is this a run at all) and the progress bar's
   * denominator (rows settling). Handing all three the WRITE count made a
   * zero-writable scope a dead Add button with no reason on it, one click after
   * a dialog that had just promised the run would happen. Strictly worse than
   * the refusal it replaced (/pr-review iter 3, Agent A N1).
   *
   * NO `> 0` GUARD, and that is a correction rather than a relapse.
   *
   * `origin/dev` guarded on the count, and iteration 3 restored the guard while
   * fixing what `total` means. Both were reasoning about the same hazard — a
   * `total === 0` scope disabling the button with nothing on screen to explain
   * it — and the guard turned out to be the wrong lever, because nulling the
   * scope does not remove that state. It moves the panel to the ID lane, where
   * the sentence is `confirmationLine`'s and CANNOT mention the filter: the
   * panel no longer knows a filter exists.
   *
   * Found by driving the built app: filter "Added to search? = Yes" on a list
   * with 114 addable rows, and the panel read "Nothing to add to search ID
   * 4001. These contacts are already in Thrive, or blocked until they are
   * fixed there." True of the four rows on screen, and read by a recruiter as
   * a claim about the list — which has 114.
   *
   * Keeping the scope at `{ total: 0 }` is what makes the honest sentence
   * reachable: the panel stays on the scope lane, `scopeConfirmationLine` says
   * "Nothing MATCHING YOUR FILTERS to add…", and the CTA is still disabled
   * because `total === 0` — the same refusal, with the scope named.
   */
  const addScope =
    addScopeChoice === "all"
      ? {
          // FILTERS AND NOTHING ELSE — this object is now a lane flag plus the
          // scope it names (step 14 of the plan; /pr-review iter 6, Agent C N1).
          // It used to carry `rowTotal`, `total` and `writable`. `rowTotal` and
          // `writable` had no reader left once the panel started taking its
          // numbers from the preview, and `total` was worse than dead: it was
          // the page's client-side `add_resolvable`, and it alone decided
          // whether a select-all run actually started, while the button beside
          // it was gated on the server's `will_dispatch`. Two numbers, one
          // question. The run total now comes from the preview, passed to
          // `exportSelected` at the click.
          filters: addScopeFilters,
        }
      : null;

  /**
   * The two numbers the scope dialog puts in front of the recruiter.
   *
   * Both are computed from the LOADED page only, which is a real limit and the
   * honest one: the page holds `listPageSize()` rows while the selection can span
   * pages, so a selected id that is not on screen cannot be classified. It is
   * counted as writable rather than dropped — the same rule the panel's own
   * settle effect follows, and for the same reason: "not loaded" is not "not
   * writable", and assuming otherwise is how a run gets truncated to its first
   * page.
   */
  const loadedById = useMemo(
    () => new Map((contactsQuery.data?.items ?? []).map((r) => [r.import_contact_id, r])),
    [contactsQuery.data]
  );
  const selectedWritableCount = useMemo(
    () =>
      [...selectedIds].filter((id) => {
        const row = loadedById.get(id);
        return row === undefined || willBeWritten(row, carriedW2[id]);
      }).length,
    [selectedIds, loadedById, carriedW2]
  );
  /** Selected rows that will NOT be written, named on their own line rather
   *  than folded into the number above.
   *
   *  It used to say "rows the recruiter CAN still rescue with a decision"
   *  (/pr-review iter 7, Agent A). That was true when the only way to land here
   *  was an undecided conflict. Since 2026-09-17 a row nobody compared lands
   *  here too, and there is no decision to make about it — the recruiter runs
   *  Compare. Two populations, two remedies, and this count cannot tell them
   *  apart, which is why the sentence it feeds names neither. */
  const selectedNotCounted = useMemo(
    () =>
      [...selectedIds].filter((id) => {
        const row = loadedById.get(id);
        if (row === undefined) return false;
        return isExportable(row) && !willBeWritten(row, carriedW2[id]);
      }).length,
    [selectedIds, loadedById, carriedW2]
  );

  // Compare uses the BROADER "nothing left to look at" denominator
  // (`total_contacts - exported_count`), NOT `exportable_remaining`: an import
  // whose only un-exported contacts are W1 duplicates has `exportable_remaining
  // === 0` but MUST stay inspectable so the recruiter can keep re-checking those
  // duplicates until they're resolved in Thrive. Gated on `detail` being loaded,
  // allowing while it loads — which is NO LONGER the same posture as
  // `exportDisabled`, and the divergence is deliberate rather than drift.
  // Compare is a dry run: starting one early costs a recruiter nothing. An Add
  // started before the export status lands produces a run whose outcome cannot
  // be attributed, which costs them the answer.
  const compareDisabled = detail !== undefined && totalContacts - exportedCount <= 0;

  // The list's ONE type (Trello #711 cl.4) — same `deriveTypeTag` the Contact
  // Lists column uses, so the badge here and the pill there can never disagree.
  const typeTag = deriveTypeTag(detail?.tags, detail?.import_format);
  const typeDef = typeTag ? TAG_DEF_BY_VALUE[typeTag] : null;
  // The three Thrive columns' context (04): the SOURCE search they describe
  // and the pull's date, which freezes "In stage". A list derived from a pull
  // (05) has no single source search (it may hold several), so its tooltips
  // say "its Thrive search" and count to this list's own date. Absent on
  // every other list.
  const thriveColumns = useMemo<ThriveColumnsContext | undefined>(() => {
    if (detail?.import_format === "thrive_search_v1") {
      return {
        importedAt: detail.created_at,
        originSearchId: detail.origin_search_id ?? null,
        originSearchName: detail.origin_search_name ?? null,
        derived: false,
      };
    }
    if (detail?.has_candidacy_data) {
      return {
        importedAt: detail.created_at,
        originSearchId: null,
        originSearchName: null,
        derived: true,
      };
    }
    return undefined;
  }, [detail]);

  // The write action names what it actually does, per list type (§13 of the
  // 2026-08-18 call). A Company Map holds REFERENCES for a near-hire, not
  // candidates: its contacts go to Thrive but must NOT join the search, and the
  // old shared "Export to Thrive" label hid that difference behind a word
  // ("export") that means nothing to a recruiter.
  // Named for its SHAPE: it carries four strings, not one, so `addLabel` read
  // like a string at every call site (Agent A 7(d)).
  //
  // It also carries `intent` — WHICH route the panel calls. That used to be a
  // separate `exportIntent` const reading the same `source` four lines below,
  // deliberately, because the copy shipped in #743 with no backend and the
  // routes shipped here; the comment there said to fold them in whichever PR
  // landed SECOND. #743 is on `dev`, so this is that PR. They are one object
  // now: a label that promises behaviour must not derive it separately from the
  // code producing it. Reading the badge instead of `source` would send a
  // `source="bct", import_format="baby_chops_v1"` row down the add-to-search
  // route and put a backchannel reference into a live search, which Thrive
  // cannot undo — which is why BOTH halves key on `source`.
  const addAction =
    // Keyed on `source`, NOT `import_format`, and NOT on `typeTag` — this is
    // the one place on the page that must mirror the BACKEND rather than the
    // badge. `export_worker/service.py:286` is the single line deciding whether
    // a candidacy is created, and it reads `source`:
    //     search_id = None if imp.get("source") == "bct" else …
    // `schemas.py:38` states the two axes are orthogonal and the uploadable
    // literals let them disagree, so a row can be `source="bct",
    // import_format="baby_chops_v1"` — which under the first version of this
    // code got "Add to search" and the add-to-search note while the worker
    // created no candidacy at all. The button promises behaviour, so
    // it must read the column that produces it. /pr-review iter 1, Agent C I1.
    //
    // `pastTense` carries NO trailing punctuation and no count. It used to end
    // in a colon, which forced `.replace(/:$/, "")` at the two call sites that
    // needed the bare phrase — a regex over copy, one edit away from silently
    // leaving "Added to the search: — open the list…" on screen. It also put a
    // colon in front of a numeral ("Added to Thrive: 2 contacts."), which is
    // not how the sentence is written in English. The counted sentence is now
    // its own function, so neither surface has to assemble copy from a
    // fragment (/pr-review iter 1, Agent A 7(a) + Agent C M6).
    isBctSource
      ? {
          label: "Add to Thrive",
          // The footer twin is selection-scoped, so "Selected" belongs after
          // the verb, not tacked onto the end of the whole phrase.
          selectedLabel: "Add Selected to Thrive",
          // The completion notice replaced the deleted "Export: Done" card, so it
          // is the LAST surface a recruiter sees for this action — leaving it
          // saying "Export complete" would put the retired verb on the one screen
          // the rename most needed to reach (Agent C M6).
          pastTense: "Added to Thrive",
          completion: (n: number) => `Added ${n} contact${n === 1 ? "" : "s"} to Thrive.`,
          // The write-outcome COLUMN header. A separate field from `pastTense`
          // because that one is written to sit in a SENTENCE — in the other
          // branch it is "Added to the search", which reads long above a column
          // of check marks. Here the two happen to coincide; they are still
          // separate fields so a copy change to one cannot silently re-word the
          // other. Same object as the rest so the header and the button can
          // never name different destinations.
          outcomeColumn: "Added to Thrive",
          note: "Creates contacts in Thrive but does not add to a search.",
          // WHICH route the panel calls. Folded in here (see the header above)
          // so the label and the write it promises are derived once, together.
          intent: "add_to_thrive" as ExportIntent,
        }
      : {
          label: "Add to search",
          selectedLabel: "Add Selected to search",
          pastTense: "Added to the search",
          completion: (n: number) => `Added ${n} contact${n === 1 ? "" : "s"} to the search.`,
          outcomeColumn: "Added to search",
          // Jared's own wording, near-verbatim from the 2026-08-20 call
          // (Trello #729 cl.8): "Adds to search and Thrive." said WHERE the
          // write lands and nothing about the two cases a recruiter actually
          // wonders about — is this person already in Thrive, and what happens
          // to the profile that is already there.
          //
          // It stays a `note`, which is NOT the same as staying a line of
          // prose: `SearchCard` renders the note as an ⓘ tooltip plus an
          // sr-only paragraph the CTA points at with `aria-describedby`. The
          // card asked for "a tooltip" and this surface has been one since the
          // cl.4 work — see `SearchCard`'s docblock for why it is BOTH.
          note:
            "If this person is already in Thrive, we will add them to the search and " +
            "update their profile with their most recent job. If not, we will add them " +
            "to Thrive and add them to the search.",
          intent: "add_to_search" as ExportIntent,
        };

  /**
   * Why the Add CTA is dead, when it is dead for a reason the recruiter can act
   * on.
   *
   * `addCountsUnknown` covers loading AND failure, and until now both rendered
   * an identical disabled button whose only accessible text was
   * `addAction.note` — which describes what Add DOES and says nothing about why
   * it is unavailable. A recruiter on a failed contacts query saw a permanently
   * dead button and no error anywhere on the page. Carried unaddressed through
   * two iterations because the gate is correct; it was the silence that was
   * wrong.
   */
  // ---------------------------------------------------------------------------
  // Person side panel (epic 2026-09-21, sub-task 05; flag `person-panel`).
  //
  // The URL is the state (`?person=&tab=`, see `usePersonPanelUrl`), and the
  // panel reads the SAME rows the table renders: `‹ ›` walk `displayItems` in
  // their on-screen order, and the open row's state pill reads that row out of
  // the contacts cache. Opening the panel touches no query key of this page,
  // so the list is not refetched.
  // ---------------------------------------------------------------------------
  // The SAME gate as `FeatureRoute` / `useFeatureAccess` (`pageVisible`):
  // `person-panel` is released on dev and false on prod, where it sits on
  // `PHASE_PAGE_IDS[2]`: `?preview=2` (and 3, 4, all; not 1) reveal the avatar
  // button and no param hides it (Jose, 2026-09-22). The mode comes from `usePreviewModeIfMounted`
  // because this page renders in dozens of suites that mount no
  // `PreviewProvider`; in the app the provider is at the root and the mode is
  // the real one. Without preview, a shared `?person=` link simply opens the
  // list with no panel.
  const personPanelEnabled = pageVisible(
    activeFlags(),
    usePreviewModeIfMounted(),
    "linkedin",
    "person-panel"
  );
  const {
    personUrl,
    openHandle,
    personStops,
    openIndex,
    openRow,
    openRowId,
    openPerson,
    closePerson,
    stepPerson,
  } = useListPersonPanel({ rows: displayItems, enabled: personPanelEnabled });
  const peopleApi = useMemo(() => peopleClient ?? defaultPeopleClient(), [peopleClient]);
  // One contact's compare, launched from the panel (Compare to Thrive, or
  // Review conflict — the compare modal is where the conflict card lives).
  // The HANDLE is captured with the id: the refresh after the modal closes
  // must target the person the compare ran for, whatever the URL says by then.
  const [panelCompare, setPanelCompare] = useState<{
    contactId: string;
    handle: string;
  } | null>(null);
  // The panel's AI Review tab has its prompt viewer up: suspend the panel
  // behind it, as for the compare modal.
  const [panelViewerOpen, setPanelViewerOpen] = useState(false);

  /** After a write on one contact: the table's rows, the detail, the run poll
   *  and every cached view of the person — the Thrive & HubSpot history too,
   *  so its "Not in Thrive" does not sit out its 60 s staleTime next to an
   *  "Added to search" pill (it may still lag by nu-sync's own sync delay).
   *  Resolves when the table's rows have been re-read (settled either way), so
   *  a caller can hold its busy state until the row reflects the write. */
  const refreshAfterPersonAction = useCallback(
    (handle: string | null): Promise<void> => {
      const rows = pageQueryClient.invalidateQueries({ queryKey: ["import-contacts", importId] });
      void pageQueryClient.invalidateQueries({ queryKey: ["import-detail", importId] });
      void pageQueryClient.invalidateQueries({ queryKey: ["imports", importId, "export"] });
      if (handle) {
        void pageQueryClient.invalidateQueries({ queryKey: personQueryKey(handle) });
        void pageQueryClient.invalidateQueries({ queryKey: relationshipQueryKey(handle) });
      }
      return rows;
    },
    [pageQueryClient, importId]
  );

  // The per-contact add, as a MUTATION keyed by contact (`personAddInFlight`),
  // so the panel's button reads "in flight" from the cache and cannot fire a
  // second add for a contact whose first is still running.
  // The mutation spans the POST AND the rows' re-read: until the refetch lands
  // the row still shows its old state, and a button re-armed on the POST's
  // return alone could send a second add in that window (/pr-review #954
  // iter 2, A #3).
  const personAdd = useMutation({
    mutationKey: PERSON_ADD_MUTATION_KEY,
    mutationFn: async ({
      contactId,
      w2Decision,
      handle,
    }: PersonAddVariables & { w2Decision?: string; handle: string | null }) => {
      const result = await api.addOneContactToSearch(importId, contactId, w2Decision);
      await refreshAfterPersonAction(handle);
      return result;
    },
  });
  const addOneFromPanel = useCallback(
    async (contactId: string, handle: string | null, w2Decision?: string) => {
      if (pageQueryClient.isMutating(personAddInFlight(contactId)) > 0) return;
      const result = await personAdd.mutateAsync({ contactId, w2Decision, handle });
      if (result.outcome === "added") toast.success("Added to the search.");
      else if (result.outcome === "pending")
        toast.info("Still adding. The row will update when the run finishes.");
      // Held back before any run (Trello #823): the way out is a Compare,
      // which the server's batch sentence ("1 contact to add was…") says
      // badly about one person.
      else if (result.skip_reason) toast.info(personAddNeedsCompareToast(result.skip_reason));
      else if (result.outcome === "skipped")
        toast.info(result.detail ? `Not added: ${result.detail}` : "Not added.");
      else throw new Error(result.detail ?? "The add failed.");
    },
    [pageQueryClient, personAdd.mutateAsync]
  );

  // What the panel's compare modal has ALREADY written, per contact. A card's
  // "Update anyway" (or "Add as new") adds the contact through the modal's own
  // call, so the modal's later "Continue to Add to search" must not add it a
  // second time — a second POST, a second export job and a "Not added" toast
  // right after the modal celebrated (/pr-review #954 iter 2, C N2). Scoped to
  // THIS host: the modal and its Continue are shared with the page's own
  // compare, whose Continue opens the batch export panel and is unchanged.
  const panelCardWritesRef = useRef<Set<string>>(new Set());
  const panelCompareClient = useMemo<ImportsClient>(
    () => ({
      ...api,
      addOneContactToSearch: async (
        ...args: Parameters<ImportsClient["addOneContactToSearch"]>
      ) => {
        const contactId = args[1];
        // Recorded when the write STARTS, so a Continue pressed while the
        // card's write is still in flight does not race a second one.
        panelCardWritesRef.current.add(contactId);
        try {
          const result = await api.addOneContactToSearch(...args);
          // A contact held back for its Compare was not written either, so a
          // Continue after the re-run must still add it (Trello #823).
          if (result.outcome === "failed" || result.skip_reason)
            panelCardWritesRef.current.delete(contactId);
          return result;
        } catch (e) {
          // A 409 "already exported" is the write being done, not failed.
          const done =
            e instanceof ImportsApiError &&
            e.status === 409 &&
            (e.detail ?? "").toLowerCase().includes("already exported");
          if (!done) panelCardWritesRef.current.delete(contactId);
          throw e;
        }
      },
    }),
    [api]
  );
  // A keyboard user starts INSIDE the panel's compare modal, not on <body>
  // with the page behind the overlays one Tab away (/pr-review #954 iter 2,
  // C N1). The drawer's own layout effect has already captured the control to
  // return to on resume; this passive effect runs after it.
  const panelCompareRef = useRef<HTMLDivElement | null>(null);
  const panelCompareOpen = panelCompare !== null;
  useEffect(() => {
    if (!panelCompareOpen) return;
    panelCardWritesRef.current = new Set();
    const dialog = panelCompareRef.current?.querySelector<HTMLElement>('[role="dialog"]');
    if (!dialog) return;
    const first = dialog.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (first) first.focus();
    else {
      dialog.tabIndex = -1;
      dialog.focus();
    }
  }, [panelCompareOpen]);

  // The per-contact add is the add-to-SEARCH route, so it has nothing to do on
  // a Company Map list (whose write is add-to-Thrive, no candidacy) or on a list
  // with no search to add to.
  const panelAddBlockedReason = isBctSource
    ? "This list adds contacts to Thrive without a search. Use Add to Thrive on the list."
    : !detail?.search_id_thrive
      ? NO_SEARCH_LINKED
      : null;

  const addGateNote = contactsQuery.isError
    ? "Add is unavailable because this list's contacts could not be loaded, so we cannot tell you what a run would write. Reload the page to try again."
    : addAction.note;
  // DECLARED AFTER `addAction`, not beside the gate it explains: reading it
  // from up there is a TDZ error, which is the third one this PR has produced
  // by placing a derivation next to its subject rather than after its inputs.

  return (
    <div data-testid="import-detail-page" className="p-8">
      {/* Header. TWO columns: everything the recruiter READS on the left,
          the one block they ACT from on the right.

          The left column stacks title-then-type-meta rather than putting the
          meta on its own full-width row below (2026-08-26). The Search block is
          ~3 lines tall and the title is one, so a single-row header left a hole
          the height of two lines under the title, with the meta stranded below
          the whole thing. Stacking fills that hole with the facts that belong
          next to the name anyway.

          Both the accent stripe and the type pill read from the ONE derived
          type tag, so they cannot disagree. The title WRAPS rather than
          truncates — a contact-list name is often a filename with the whole
          pipeline encoded in it, and the tail is the part that identifies it. */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div
          data-testid="import-header"
          data-type={typeTag ?? undefined}
          className={`flex min-w-0 flex-1 flex-col gap-y-2 border-l-4 pl-3 ${
            typeDef?.theme.accentBorder ?? "border-border"
          }`}
        >
          {/* Row 1 — the name, and the three affordances that act on THIS
              list: rename, stats, history. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <div className="min-w-0">
              <EditableImportTitle
                importId={importId}
                filename={detail?.filename}
                canRename={detail?.can_rename}
                client={api}
              />
            </div>
            {detail && <ImportStatsPopover detail={detail} exportStatus={exportStatus} />}
            <FullHistory client={api} importId={importId} combineRecipe={detail?.combine_recipe} />
          </div>

          {/* Row 2 — the type pill + the facts only that type has. Every
              variant takes the same shape so the badge never appears to move
              by kind (see `TypeMetaLine`). */}
          {detail && (
            <TypeMetaLine
              typeDef={typeDef ?? undefined}
              label={typeTag ? typeLabel(typeTag, detail.import_format) : undefined}
            >
              {isBctFormat ? (
                <BctRunMeta triggeredByEmail={detail.created_by ?? detail.uploader_email ?? null} />
              ) : isAiReviewed ? (
                <AiReviewMeta reviewedBy={detail.created_by ?? detail.uploader_email ?? null} />
              ) : thriveColumns && !thriveColumns.derived ? (
                // A list derived from a pull keeps its own wording (05).
                <ThriveImportMeta
                  originSearchId={thriveColumns.originSearchId}
                  originSearchName={thriveColumns.originSearchName}
                  pulledBy={detail.owner_name ?? detail.uploader_email ?? null}
                  createdAt={detail.created_at}
                />
              ) : (
                <RawImportMeta
                  uploader={detail.owner_name ?? detail.uploader_email ?? null}
                  createdAt={detail.created_at}
                />
              )}
            </TypeMetaLine>
          )}
        </div>
        {detail && (
          <div className="w-full shrink-0 sm:w-auto sm:max-w-sm">
            <SearchCard
              detail={detail}
              bullseyeHref={bullseyeHref}
              actionNote={addGateNote}
              actionNoteId={addActionNoteId}
              actions={
                <>
                  <Button
                    variant="secondary"
                    type="button"
                    data-testid="compare-to-thrive"
                    disabled={compareDisabled}
                    onClick={() => {
                      // Reset a stale "all" choice from a previous launch —
                      // each confirmation starts from the current selection.
                      setCompareAll(false);
                      // The confirmation is SKIPPED when it has nothing to ask:
                      // one page, everything ticked, so "all" has exactly one
                      // possible meaning. See
                      // `shouldSkipPreCompareConfirmation` for why that is a
                      // narrowing of the modal's own collapse rule rather than
                      // a second rule beside it.
                      if (skipPreCompareConfirmation) setCompareOpen(true);
                      else setCompareConfirmOpen(true);
                    }}
                  >
                    Compare to Thrive
                  </Button>
                  <ActionArrow />
                  <Button
                    type="button"
                    data-testid="export-to-thrive-cta"
                    aria-describedby={addActionNoteId}
                    disabled={exportDisabled}
                    // NOT COMPARED YET → SHAKE AND SAY SO, rather than open a
                    // panel whose only content is an explanation of why it
                    // cannot do anything (operator, 2026-09-15). The panel had
                    // to be read, understood and closed to learn one fact the
                    // button could state in place.
                    //
                    // The SAME gesture the conflict cards use when Thrive comes
                    // back still-colliding — one rejection movement in the
                    // product, imported rather than re-tuned. It never carries
                    // the message on its own: `runShake` is a no-op under
                    // reduced motion, and a wobble says "no" without saying
                    // what to do, so the toast is the load-bearing half.
                    //
                    // `compared_at` is non-null ONLY for a run that SUCCEEDED
                    // (null while one is in flight, and for one that failed),
                    // which is the same question the export panel's own
                    // `compareBlockReason` answers with more words for the four
                    // reasons it distinguishes. This button needs one bit.
                    // `event.currentTarget`, not a ref: the `Button` primitive
                    // does not forward one, and the element that was pressed is
                    // already in hand. Adding `forwardRef` to a design-system
                    // primitive to reach a node the event carries is blast
                    // radius bought for nothing.
                    onClick={(event) => {
                      if (!compareHasRun) {
                        runShake(event.currentTarget);
                        toast.warning(COMPARE_FIRST_TOAST);
                        return;
                      }
                      openExportFrom(carriedW2);
                    }}
                  >
                    {/* The label does NOT change when everything is written
                        (Lucas, 2026-09-09). It used to swap to "All added to
                        the search", which renamed the control the moment it
                        went dead: a button that says one thing on Monday and
                        another on Tuesday is harder to find than a greyed-out
                        one that still says what it does. The disabled state
                        already carries "nothing left to add". */}
                    {addAction.label}
                  </Button>
                </>
              }
            />
          </div>
        )}
      </div>

      {/* Criteria — its own full-width row because it is the only header fact
          that grows without bound, and the only one that differs per type. It
          carries the type tint the Search block used to.

          The capture query goes FIRST and in raw's palette, on every type that
          carries one: a Company Map and an AI Review child inherit it from the
          parent they were derived from, and where the people came from precedes
          what was then done to them. `SearchCriteriaBox` has no `theme` prop —
          the palette is structural, not a decision made here. It returns null
          when there is nothing to show, so a raw import from before migration
          0036, a file whose CSV had no such column, and an inline Send-to-NU-
          Assist upload all render no row at all. Trello #732. */}
      {detail && <SearchCriteriaBox searchCriteria={detail.search_criteria} />}
      {detail && isBctFormat && (
        <BctCriteriaBox importId={importId} bctClient={bctApi} theme={typeDef?.theme} />
      )}
      {detail && !isBctFormat && (isAiReviewed || isAiReviewFormat) && (
        <AiReviewCriteriaBox
          columns={detail.annex_columns ?? []}
          // cl.5: the SENTENCES when the run resolves, #780's column chips when
          // it does not. `isAiReviewFormat` widens #780's `isAiReviewed` gate so
          // a review file states its criteria whether or not its score columns
          // have come back.
          //
          // What that widening actually buys is NARROW, and an earlier version
          // of this comment oversold it (round 9, Agent A §3.2). It claimed to
          // cover "the whole time the run is scoring" — it does not:
          // `reviewCriteria` comes from `by-import`, which the repository
          // constrains to `applied`, and a still-`parsing` child has no
          // `annex_columns` either, so BOTH arms are empty and the row renders
          // nothing for that entire window. The real gain is the cache-refresh
          // gap after `applied` lands but before `detail` has picked up the
          // score columns.
          criteria={reviewCriteria}
          criteriaLoading={reviewQuery.isLoading}
          canFeedAi={detail.can_feed_ai && isAiReviewFormat}
          // BOTH params, and they still say different things — that is why one
          // button sends both. `import` names the list to SCORE; `criteriaFrom`
          // names where the criteria come from. For a rerun they are the same
          // file, so the recruiter lands with the list already selected AND the
          // criteria pinned.
          //
          // The pin is the whole point, and it is what fixes the reported bug.
          // With `?import=` alone the criteria arrive as a PREFILL owned by the
          // file (`AiReviewPage.criteriaSeededFromRef`), and the selection
          // effect discards an untouched prefill the moment that file stops
          // being selected — so changing the people list ate the criteria the
          // recruiter had just come here to reuse. Carrying `criteriaFrom`
          // pins them from the first render, which is the path live QA case C1
          // and `AiReviewPage.test.tsx`'s `KEEPS them when the carry source IS
          // the selected list` already prove survives exactly that transition.
          // Nothing in the clearing effect had to change.
          //
          // This used to be TWO buttons — `Edit criteria and rerun` here and
          // `Run on another list…` beside it, differing only in which param
          // they sent. One door, per the same decision that removed the
          // in-page third one (PRD 2026-09-10, Decision 4).
          onRerun={() =>
            navigate(`/research/ai-review?import=${importId}&criteriaFrom=${importId}`)
          }
          theme={typeDef?.theme}
        />
      )}

      {detailQuery.isError && (
        <p data-testid="detail-error" className="text-body text-error">
          This people list could not be loaded.
        </p>
      )}

      {detail && (
        <div className="flex flex-col gap-4">
          <PipelineStatus detail={detail} />
          {/* "N already in search — Remove them" (epic 2026-09-22, sub-task
              05, Flow A). Gated on `import_format === "combined"` here, at the
              one render site — `compareBuckets` itself is captured from ANY
              whole-import/selection Compare that succeeds (see the host
              above), so this is what keeps a manual Compare on a non-combined
              import from growing a banner it never had. */}
          {detail.import_format === "combined" &&
            compareBuckets &&
            inSearchCount(compareBuckets) > 0 && (
              <AlreadyInSearchBanner
                client={api}
                importId={importId}
                count={inSearchCount(compareBuckets)}
                onRemoved={() => setCompareBuckets(null)}
              />
            )}
          {/* Gated on `exportInProgress`, NOT on `hasExportActivity` — that one
              word is the whole fix (Trello #711 cl.4 + /pr-review iter 1).

              What Jared asked to remove was PERSISTENT state: a "Done — 2 of 2
              processed" bar that outlived its run and sat on the page forever
              restating a number the Contacts card already carries. A RUNNING
              indicator is a different thing, and the first version of this
              checklist deleted both — which, combined with the panel being
              mounted `{exportOpen && …}`, meant closing the modal mid-run left
              the page with no progress AND no completion notice at all.

              So: this appears while contacts are actually mid-flight and
              disappears the moment the run goes terminal, when the transient
              toast (the page-owned effect above) takes over and the durable
              count lands in the Contacts card. Nothing outlives its run. */}
          {exportInProgress && (
            <div
              data-testid="export-running"
              className="flex flex-col gap-1.5 rounded-md border border-border bg-bg-subtle px-4 py-3"
            >
              <div className="flex items-center gap-2 text-label text-text">
                <span>{addAction.label} — running…</span>
                {exportRequested > 0 && (
                  <span className="text-text-secondary">
                    {exportProcessed} of {exportRequested} processed
                  </span>
                )}
              </div>
              {exportRequested > 0 ? (
                <ProgressBar
                  value={exportProcessed}
                  max={exportRequested}
                  label="Export progress"
                  live
                />
              ) : (
                <ProgressBar indeterminate live label="Export progress" />
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3">
        {/* A file the recruiter was sent to before it existed has to say what
            is happening to it. Three states, and the AI Review ones only exist
            because `?run=` was handed over on the 202 — without it this page
            cannot ask about its own run at all (see `arrivedWithRunId`). */}
        {/* `!aiReviewFailureMessage` (round 8, Agent C M4): the child's status
            and the run's failure are written in one server transaction, but the
            DETAIL query has its own cadence, so for one poll interval this
            block could render "contacts will appear here as soon as it
            finishes" directly above the red banner saying it did not. */}
        {parseStatus === "parsing" && !aiReviewFailureMessage && (
          <div
            data-testid="import-parsing"
            className="flex flex-col gap-1.5 rounded-md border border-border bg-bg-subtle px-4 py-3 text-body-sm text-text-secondary"
          >
            <span>
              {aiReviewSaving ? (
                "Saving your people list. The scores are in, and this is the file being built…"
              ) : aiReviewWaitingMessage ? (
                <>
                  {aiReviewWaitingMessage}
                  {arrivingRun?.progress
                    ? ` ${arrivingRun.progress.scored} of ${arrivingRun.progress.total} scored so far.`
                    : ""}
                </>
              ) : aiReviewInFlight ? (
                <>
                  Scoring candidates against your criteria
                  {arrivingRun?.progress
                    ? `: ${arrivingRun.progress.scored} of ${arrivingRun.progress.total}`
                    : ""}
                  …
                </>
              ) : (
                "Parsing people list. Contacts will appear here as soon as it finishes…"
              )}
            </span>
            {(() => {
              const label = aiReviewSaving
                ? "Saving your people list"
                : aiReviewWaitingMessage
                  ? "Waiting for the AI provider"
                  : aiReviewInFlight
                    ? "Scoring candidates"
                    : "Parsing people list";
              // Scoring with a known count draws the real fraction — the text
              // above already says "4 of 14", so a sliding bar would contradict it.
              const scoring = !aiReviewSaving && (aiReviewWaitingMessage || aiReviewInFlight);
              const progress = scoring ? arrivingRun?.progress : undefined;
              return progress && progress.total > 0 ? (
                <ProgressBar value={progress.scored} max={progress.total} live label={label} />
              ) : (
                <ProgressBar indeterminate live label={label} />
              );
            })()}
          </div>
        )}

        {/* A combined import the sweeper failed (epic 2026-09-22, sub-task
            05): `ImportDetail.parse_error` for a sweep-triggered failure is
            the FIXED GENERIC line, not a knowable reason
            (`combine_worker`'s sweeper records no finer-grained one — see
            sub-task 02's own PRD) — so `PipelineStatus`'s existing
            `parse-error-reason` paragraph would show text that does not
            name what actually happened. This says so instead, and
            `PipelineStatus` suppresses its reason line for a combined row so
            the page shows ONE failure message (/pr-review C-M7); its status
            pill is unchanged. No retry
            action: re-running a recipe is out of scope in v1 (no
            "Duplicate" affordance), so the recruiter's only real next step
            is Combine & Filter again from scratch — which the copy names. */}
        {parseStatus === "failed" && detail?.import_format === "combined" && (
          <div
            data-testid="combined-failed-notice"
            className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
          >
            Combining stopped — try again.
          </div>
        )}

        {/* The failure the redirect would otherwise make silent. Before this,
            a failed run left the recruiter on a "Failed" pill above an EMPTY
            table, with a Contacts card still showing the parent's contact
            count — and no reason anywhere. `aiReviewFailureText` already
            separates a scoring failure from an apply failure; reused. */}
        {aiReviewFailureMessage && (
          <div
            data-testid="ai-review-run-failed"
            className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
          >
            {aiReviewFailureMessage}
          </div>
        )}

        {/* The run stopped reporting progress and this page is where the
            recruiter is standing. It offers no retry — recovery is
            operator-only and a button would spend Bedrock twice — and the poll
            keeps running underneath at a slower cadence, so a slow-but-healthy
            run still lands and this banner still goes away on its own. Say
            that, rather than "we've stopped waiting", which was wrong on the
            other page for three iterations. */}
        {arrivingRunAbandoned && (
          <div
            data-testid="ai-review-run-abandoned"
            className="rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
          >
            {arrivingRunPollFailed
              ? "We lost contact with the server while this review was running. "
              : "This review is taking longer than expected. "}
            We are still watching, and the scores will appear here if it finishes. You can leave
            this page — the people list is already on{" "}
            <Link to={backToListPath} className="underline hover:no-underline">
              People Lists
            </Link>{" "}
            and will show as failed there if it could not be built.
          </div>
        )}

        {contacts &&
          parseStatus !== "parsing" &&
          (() => {
            const displayTotal = contacts.total;
            const displayPage = contacts.page;
            const displayPageSize = contacts.page_size;
            return (
              <>
                <ContactsTable
                  compareHasRun={compareHasRun}
                  contacts={displayItems}
                  columns={columns}
                  nameQuery={nameQuery}
                  onNameQueryChange={changeNameQuery}
                  emailFilter={isBctFormat ? "" : emailFilter}
                  onEmailFilterChange={isBctFormat ? undefined : changeEmailFilter}
                  reviewStatusFilter={reviewStatusFilter}
                  onReviewStatusFilterChange={changeReviewStatusFilter}
                  dupLinkedinFilter={dupLinkedinFilter}
                  onDupLinkedinFilterChange={changeDupLinkedinFilter}
                  thriveIdFilter={thriveIdFilter}
                  onThriveIdFilterChange={changeThriveIdFilter}
                  addToSearchFilter={addToSearchFilter}
                  onAddToSearchFilterChange={changeAddToSearchFilter}
                  skippedFilter={skippedFilter}
                  onSkippedFilterChange={changeSkippedFilter}
                  onUndoLeaveOut={undoLeaveOutFromTable}
                  onUndoDifferentPerson={undoDifferentPersonFromTable}
                  enrichmentFilter={enrichmentFilter}
                  onEnrichmentFilterChange={changeEnrichmentFilter}
                  currentPositionFilter={currentPositionFilter}
                  onCurrentPositionFilterChange={changeCurrentPositionFilter}
                  currentCompanyFilter={currentCompanyFilter}
                  onCurrentCompanyFilterChange={changeCurrentCompanyFilter}
                  bctCompanyFilter={bctCompanyFilter}
                  onBctCompanyFilterChange={changeBctCompanyFilter}
                  bctPositionFilter={bctPositionFilter}
                  onBctPositionFilterChange={changeBctPositionFilter}
                  bctYearFrom={bctYearFrom}
                  onBctYearFromChange={changeBctYearFrom}
                  bctYearTo={bctYearTo}
                  onBctYearToChange={changeBctYearTo}
                  counts={contacts.counts}
                  selectedIds={selectedIds}
                  onToggleSelect={selection.toggle}
                  onToggleSelectAll={selection.setPage}
                  selectionMode={selection.mode}
                  totalFilteredContacts={filteredTotal}
                  onSelectAllFiltered={selection.selectAllFiltered}
                  onClearCrossPageSelection={selection.clear}
                  sort={sort}
                  onSort={handleSort}
                  rationaleFor={rationaleFor}
                  notesQuery={notesQuery}
                  // The box only where there are notes to search: a list
                  // whose score columns resolved. A Company Map list never
                  // has them.
                  onNotesQueryChange={isAiReviewed && !isBctFormat ? changeNotesQuery : undefined}
                  notesTerms={notesTerms}
                  // Hidden while the rows on screen still belong to the
                  // previous terms (`keepPreviousData`), so the line never
                  // pairs new words with an old count.
                  notesMatchCount={
                    notesTerms.length > 0 && !contactsQuery.isPlaceholderData
                      ? {
                          count: displayTotal,
                          of: otherFiltersActive ? null : totalContacts,
                          terms: notesTerms,
                        }
                      : null
                  }
                  resolvedAvatars={resolvedAvatars}
                  thriveColumns={thriveColumns}
                  stageFilter={stageFilter}
                  onStageFilterChange={changeStageFilter}
                  thriveAvatars={isThriveSourced ? thriveAvatars : undefined}
                  isBct={isBctFormat}
                  // Scopes the column-width measurement to THIS import.
                  //
                  // An earlier version of this comment rejected `key={importId}`
                  // as the alternative — "it would work, but by remounting".
                  // There IS a `key={importId}` on this same element, a few
                  // lines below, and it predates this card (#685); the page
                  // acknowledges it further up. So the alternative was never
                  // available to reject, and the prop is component-level
                  // defence rather than the thing that scopes the cache. It
                  // still earns its place: the unit tests render
                  // `<ContactsTable>` with no key, so it guards something real
                  // (`/pr-review` round 11, Agent A MINOR 10).
                  importId={importId}
                  // Always the server-side contactsQuery — clientMode was
                  // removed when sort became server-side for all import types.
                  rowsArePlaceholder={contactsQuery.isPlaceholderData}
                  // From `addAction`, and therefore from `source` — NOT from
                  // `isBctFormat` above it. The column reports what the export
                  // worker did, and the worker reads `source`.
                  writeOutcomeLabel={addAction.outcomeColumn}
                  onOpenPerson={personPanelEnabled ? openPerson : undefined}
                  openPersonHandle={openHandle}
                  openPersonRowId={openRowId}
                  key={importId}
                />
                {/* Scores exist only on AI-reviewed lists; a Company Map list
                    has no score column to explain (F9). */}
                {!isBctFormat && (isAiReviewed || isAiReviewFormat) ? <ScoreLegend /> : null}
                <TablePagination
                  page={displayPage}
                  pageSize={displayPageSize}
                  total={displayTotal}
                  onPageChange={setPage}
                  noun="contact"
                  testId="pagination"
                />
              </>
            );
          })()}
      </div>

      {/* Footer actions */}
      <div className="mt-6 flex items-center justify-between">
        <Link
          to={backToListPath}
          data-testid="back-to-imports"
          className="text-caption text-text-secondary hover:text-accent"
        >
          ← Back to People Lists
        </Link>
        <div className="flex flex-col items-end gap-1">
          {/* The "Bulk actions can't target a filtered cross-page selection
              yet" notice used to live here, and it is GONE rather than
              reworded. It described a real gap — `select_all` could say "every
              contact" and nothing else, so a cross-page selection under a
              filter had no honest write shape and the CTA was disabled instead
              of writing 1,300 contacts while the banner claimed the filtered
              230. The request now carries the table's own `ContactFilters`, so
              the shape exists and the scope dialog names it: "Add all N
              matching your filters". */}
          <button
            type="button"
            data-testid="export-selected"
            // Opens the Export panel seeded with this page's selection. Gated by an
            // empty selection, and by the re-export guard once everything is
            // exported (Compare conflicts never block — FR-13). A cross-page
            // selection is additionally gated on having an honest write shape —
            // see the copy above.
            // `hasNarrowingFilters` is no longer part of this: a filtered
            // cross-page selection HAS a write shape now. What remains is the
            // empty-selection gate and the re-export guard.
            disabled={
              (inAllFilteredMode ? (filteredTotal ?? 0) === 0 : selectedIds.size === 0) ||
              exportDisabled
            }
            onClick={() => openExportFrom(carriedW2)}
            className="rounded-md bg-accent px-4 py-2 text-caption text-bg disabled:bg-surface disabled:text-text-muted"
          >
            {/* Same verb as the Search-card CTA. Leaving this one saying "Export
                Selected to Thrive" would put two different names for one action
                on one page — which is the confusion §8 of the call was about. */}
            {addAction.selectedLabel}
            {inAllFilteredMode && (filteredTotal ?? 0) > 0
              ? ` (all ${filteredTotal})`
              : selectedIds.size > 0
                ? ` (${selectedIds.size})`
                : ""}
          </button>
        </div>
      </div>

      {/* Pre-compare confirmation (fu4 sub-task 13): EVERY compare launch
          routes through this modal — scope choice (selection vs everyone),
          coarse time estimate, and the never-compared / stale-compared
          coverage nudge. Only its "Run compare" opens the run modal below;
          Cancel/Escape close it with nothing enqueued. */}
      {compareConfirmOpen && (
        <PreCompareConfirmation
          client={api}
          importId={importId}
          selectedScope={preCompareSelectedScope}
          allScope={preCompareAllScope}
          allTotal={totalContacts}
          onCancel={() => setCompareConfirmOpen(false)}
          onRun={(preChoice) => {
            setCompareAll(preChoice === "all");
            setCompareConfirmOpen(false);
            setCompareOpen(true);
          }}
        />
      )}

      {/* Compare dry-run modal (mounted only while open). It mounts straight
          into the run now: the "preparing" and "narrowing failed" states existed
          only to cover the full-list fetch that used to run first, and there is
          no fetch to wait for or to fail. A whole-import Compare is once again
          what it is on a never-exported import — open, and it starts. */}
      {compareOpen && (
        <CompareModalHost
          client={api}
          importId={importId}
          contactIds={compareScope.contactIds}
          autoStart={compareScope.autoStart}
          alreadyExportedCount={compareScope.alreadyExportedCount}
          scopeTotal={compareScope.scopeTotal}
          pendingOverride={comparePendingOverride}
          scope={compareScope.scope}
          nothingToCompare={compareScope.nothingToCompare}
          // KEEPS THE ANSWERS. Since the modal's primary action became the
          // re-run, closing is the documented way out for a recruiter who
          // decides to leave some rows unresolved — and `carriedW2` is what
          // feeds the page's own `Add to search`, so dropping them here would
          // make that exit cost the sitting's work.
          onClose={(w2Decisions) => {
            if (w2Decisions) setCarriedW2(w2Decisions);
            setCompareOpen(false);
          }}
          onCompareSucceeded={(buckets) => {
            setCompareSucceededHere(true);
            // Epic 2026-09-22, sub-task 05 — feeds `AlreadyInSearchBanner`,
            // which the page renders only for a combined import (see its
            // render site). Captured ONLY for a WHOLE-IMPORT run (iter-1
            // review, finding C-I4): the "Remove them" bulk action
            // (`bulk_leave_out_by_bucket`) flips the WHOLE `compare_bucket`
            // for this import, not just the rows a selection-scoped run just
            // compared — a selection run's own `already_in_search` count can
            // disagree with what the button would actually remove (e.g. a
            // 40-row whole-import bucket, then a later 5-row selection run
            // reporting "3", with "Remove them" still flipping all 40). A
            // selection-scoped run simply leaves whatever banner state (if
            // any) already exists untouched, rather than showing a count the
            // bulk action would not honour. Gated here, at the ONE host used
            // by both the manual button and the auto-Compare effect (the
            // latter is always whole-import — no table selection exists yet
            // on a fresh arrival), so this stays a single implementation.
            if (compareScope.scope === "import") {
              setCompareBuckets(buckets ?? null);
            }
          }}
          onContinueToExport={openExportFrom}
          isBct={isBctSource}
          // The verb comes from the same object the CTA uses, so the modal
          // cannot name a different destination than the button behind it.
          actionLabel={addAction.label}
        />
      )}

      {/* Export panel (mounted only while open), seeded with the table selection
          + any W2 decisions carried from Compare. */}
      {/* The scope gate. Every route into Add passes through it, and the
          no-selection case the Search-card CTA always is stays one click —
          but NOT via the collapse, which Add cannot reach and which was
          deleted in iter 2. `hasSelection: false` renders no radio group at
          all, which is a different mechanism with the same feel. This comment
          still described the collapse four lines from the one corrected for
          exactly that (/pr-review iter 3, Agent C M2). */}
      {preAddOpen && (
        <PreAddConfirmation
          selectedCount={selectedWritableCount}
          // What the REQUEST carries, which is not what the dialog promises:
          // undecided conflicts are subtracted from the promise and still sent,
          // because they carry the decision. The 200-id cap is a limit on the
          // request, so it has to read this one.
          selectedPayloadCount={selectedIds.size}
          allCount={addWritableCount}
          // Rows the run dispatches. With `resolvable` in hand the dialog can
          // say WHY nothing will be written instead of guessing at one of four
          // possible causes.
          allResolvableCount={addResolvableCount}
          // The CTA counts ROWS and the dialog counts WRITES; this is what
          // lets the dialog name the gap instead of leaving it to be found
          // after the run.
          allScopeTotal={filteredTotal ?? 0}
          notCountedCount={selectedNotCounted}
          // `hasSelection` is gone. It was `!inAllFilteredMode &&
          // selectedIds.size > 0` — the identical first two conjunctions of
          // `addNeedsScopeChoice`, which now decides whether this dialog opens
          // at all. So it could only ever arrive `true`, and the statement-only
          // arm it selected was unreachable UI.
          filterActive={hasNarrowingFilters}
          onCancel={() => setPreAddOpen(false)}
          onRun={(choice) => {
            setAddScopeChoice(choice);
            setPreAddOpen(false);
            setExportOpen(true);
          }}
        />
      )}

      {openHandle && (
        <PersonPanel
          listTitle={displayTitle(detail?.filename) || "this list"}
          position={openIndex >= 0 ? { index: openIndex, total: personStops.length } : null}
          // `isPending`, not `!data`: an errored contacts query has no data
          // either, and "…" would then wait for rows that are not coming.
          positionLoading={contactsQuery.isPending}
          positionError={contactsQuery.isError && !contactsQuery.data}
          suspended={panelCompare !== null || panelViewerOpen}
          onPrev={() => stepPerson(-1)}
          onNext={() => stepPerson(1)}
          onClose={closePerson}
          pagePath={
            personPagePath(openHandle) +
            (personUrl.tab === "profile" ? "" : `?tab=${personUrl.tab}`)
          }
          shareLinks={[
            {
              id: "list",
              label: "In this list",
              description: `Opens “${displayTitle(detail?.filename) || "this list"}” with this person open on this tab.`,
              url: personShareUrl(
                personInListPath(importId, openHandle, personUrl.tab, searchParams)
              ),
            },
            {
              id: "page",
              label: "Contact detail",
              description:
                "The person on their own, whatever list they came from. Keyed by the LinkedIn URL, so it works across lists and searches.",
              url: personShareUrl(
                `${personPagePath(openHandle)}${personUrl.tab === "profile" ? "" : `?tab=${personUrl.tab}`}`
              ),
            },
          ]}
        >
          {(nameId) => (
            <PersonView
              key={openHandle}
              client={peopleApi}
              handle={openHandle}
              aiReviewClient={aiReviewApi}
              onModalChange={setPanelViewerOpen}
              importId={importId}
              tab={personUrl.tab}
              onTabChange={personUrl.setTab}
              mode="list"
              row={openRow}
              avatarUrl={
                openRow
                  ? rowAvatarUrl(
                      openRow,
                      resolvedAvatars,
                      isThriveSourced ? thriveAvatars : undefined
                    )
                  : undefined
              }
              nameId={nameId}
              actions={{
                onCompare: (contactId) => setPanelCompare({ contactId, handle: openHandle }),
                onAddToSearch: (contactId) => addOneFromPanel(contactId, openHandle),
                addLabel: addAction.label,
                addBlockedReason: panelAddBlockedReason,
                addTargetSearchId: panelAddBlockedReason
                  ? null
                  : (detail?.search_id_thrive ?? null),
                actingOn: {
                  listName: displayTitle(detail?.filename) || "this list",
                  searchLabel:
                    panelAddBlockedReason || !detail?.search_id_thrive
                      ? null
                      : [`Search ${detail.search_id_thrive}`, detail.search_name?.trim()]
                          .filter(Boolean)
                          .join(" · "),
                  note: panelAddBlockedReason,
                },
              }}
            />
          )}
        </PersonPanel>
      )}

      {/* One contact's compare, from the panel. The same host and modal as the
          page's own Compare, scoped to one id — so the conflict card, the
          re-run and the invalidations are the page's, not a copy. "Continue to
          Add to search" adds THIS contact (with the decision taken in the
          card), not the page's selection. */}
      {panelCompare && (
        <div ref={panelCompareRef}>
          <CompareModalHost
            client={panelCompareClient}
            importId={importId}
            contactIds={[panelCompare.contactId]}
            autoStart
            alreadyExportedCount={0}
            scopeTotal={1}
            scope="selection"
            nothingToCompare={false}
            isBct={isBctSource}
            actionLabel={addAction.label}
            // A ONE-CONTACT run sets the list-wide "compare has run" flag, and
            // that is deliberate: it mirrors the server, where `compared_at`
            // comes from the import's `dedup_status`, which a subset run writes
            // too. The page's own Add then stops asking for a compare first, as
            // it would after a reload.
            onCompareSucceeded={() => setCompareSucceededHere(true)}
            onClose={() => {
              const { handle } = panelCompare;
              setPanelCompare(null);
              void refreshAfterPersonAction(handle);
            }}
            onContinueToExport={(w2Decisions) => {
              const { contactId, handle } = panelCompare;
              setPanelCompare(null);
              // Blocked list, or the card already wrote this contact: nothing
              // left to add, only the views to refresh.
              if (panelAddBlockedReason || panelCardWritesRef.current.has(contactId)) {
                void refreshAfterPersonAction(handle);
                return;
              }
              void addOneFromPanel(contactId, handle, w2Decisions[contactId]).catch((e: unknown) =>
                toast.error(e instanceof Error ? e.message : "The add failed.")
              );
            }}
          />
        </div>
      )}

      {exportOpen && (
        <div
          // biome-ignore lint/a11y/useSemanticElements: same conditionally-
          // rendered overlay pattern as CompareToThriveModal — role="dialog" +
          // aria-modal is the accessible fit here.
          role="dialog"
          aria-modal="true"
          aria-label="Export selected contacts to Thrive"
          data-testid="export-panel-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
            <div className="flex items-center justify-end border-b border-border p-2">
              <Button
                variant="secondary"
                data-testid="export-panel-close"
                onClick={() => setExportOpen(false)}
              >
                Close
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ExportToThrivePanel
                client={api}
                importId={importId}
                intent={addAction.intent}
                initialSelectedIds={selectedIds}
                // See `addScope` for why an EMPTY open is a scope, not a seed.
                selectAllScope={addScope}
                // The scope sentence names the filter here now: the dialog that used
                // to carry that clause is no longer on this route.
                scopeFilterActive={hasNarrowingFilters}
                // The row count the footer CTA quotes, so the panel can reconcile its
                // own (smaller) number against it. The scope dialog used to do this
                // and is no longer on these routes.
                initialW2Decisions={carriedW2}
                // The way out of the panel's blocked state (Trello #758): with
                // no usable compare there are no numbers to show and the write
                // is held, so the only useful action is the one the recruiter
                // came here without doing.
                //
                // It runs the SAME launch sequence as the Search-card button,
                // confirmation and all — a second, quieter path into Compare
                // would be a second place for the scope rules to drift.
                onRequestCompare={() => {
                  setExportOpen(false);
                  setCompareAll(false);
                  if (skipPreCompareConfirmation) setCompareOpen(true);
                  else setCompareConfirmOpen(true);
                }}
                // The destination for the panel's confirmation line. Only the
                // page holds the detail row, and the panel must not fetch it a
                // second time to name the search the CTA above it already links.
                searchIdThrive={detail?.search_id_thrive ?? null}
                searchName={detail?.search_name ?? null}
                // One verb across the whole flow: the two CTAs that open this
                // panel, its own heading and submit button, and the toast it
                // fires while open all read from `addAction`.
                actionLabel={addAction.selectedLabel}
                // Mid-run, in the same verb as the heading, the two CTAs and
                // the page's own progress line (Agent C M3).
                runningLabel="Adding…"
                successMessage={addAction.completion}
                // Same wording the page uses for its own untrusted-snapshot
                // case, so a recruiter gets one sentence for one situation
                // whichever surface happens to be speaking.
                // Same refusal of the verb, for the same reason: this message
                // is for a run whose outcome the panel could not attribute.
                incompleteMessage={"The run finished. Open the contacts table to see the results."}
                // The page no longer keeps a persistent export card (cl.4), so
                // the terminal outcome has to reach the recruiter some other
                // way. `notify` was always a prop here and was never passed —
                // the card was doing that job. Wire it to the app-wide sonner
                // portal mounted in `main.tsx`.
                notify={(message, kind, origin) => {
                  // Only a TERMINAL notice means the recruiter has been told how
                  // the run ended. The hook also speaks when the POST itself
                  // fails, and counting that as "already told" suppressed the
                  // real completion toast of a run still in flight.
                  if (origin === "terminal") hookSpokeRef.current = true;
                  // Three kinds, not two. The hook's "terminal, but the payload
                  // carries none of this run's rows" case is an UNKNOWN outcome,
                  // not a failure, and routing it through `toast.error` painted
                  // a destructive red banner over what may well have been a
                  // clean success (/pr-review iter 1, Agent A 2(c) / Agent C M2).
                  if (kind === "error") toast.error(message);
                  else if (kind === "info") toast.info(message);
                  else toast.success(message);
                }}
                onTerminal={() => {
                  void contactsQuery.refetch();
                  void detailQuery.refetch();
                  // Clear the listing's selection too: most just-run rows now read
                  // `exported`/`skipped` and isExportable() disables them, so
                  // leaving them checked would only invite a redundant re-export.
                  // The exception proves the rule — a W2 row the run declined stays
                  // SELECTABLE (its "Update anyway" is still open), and it is exactly
                  // that row that must not stay silently checked into the next run.
                  // Covers BOTH modes — a terminal cross-page run must drop the
                  // "all N selected" claim just like an explicit one drops its ids.
                  selection.clear();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
